import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import { DateTime } from 'luxon';
import * as _ from 'lodash';
import { AuthService } from '../../@services/auth.service';
import { UsersService } from '../../@services/users.service';
import { agGridThemes } from '../../@shared/ag-grid-themes';
import { NgxCsvParserModule, NgxCsvParser } from 'ngx-csv-parser';
import { ToastService } from '../../@services/toast.service';
import { SetFloatingFilter } from '../../@shared/set-floating-filter.component';
import { ScannersService } from '../../@services/scanners.service';
import { CustomerDirectoryService } from '../../@services/customer-directory.service';
import { ActivatedRoute } from '@angular/router';
import { ClaimsEditorComponent } from '../shared/claims-editor/claims-editor.component';
import { InlineScannerClaimsComponent } from '../shared/inline-scanner-claims/inline-scanner-claims.component';
import { ChannelsEditorComponent } from '../shared/channels-editor/channels-editor.component';
import { AccessEditorComponent } from '../shared/access-editor/access-editor.component';
import { WelcomeEmailModalComponent } from './welcome-email-modal/welcome-email-modal.component';
import { WelcomeEmailService, WelcomeEmailUserInput } from '../../@services/welcome-email.service';
import { SseBurstBreaker } from '../shared/sse-burst-breaker';
import { SyncStateService } from '../shared/sync-state.service';

const localLog = console.log;
// Live toggle persistence. Default ON — operators expect the users
// grid to reflect Cognito reality at all times. Mirrors the pattern
// from /admin/error-reports but inverted-default (errorReports defaults
// off so a fresh operator sees a static snapshot first; the users grid
// defaults on because every operator's first action is usually an edit).
const LIVE_LOCALSTORAGE_KEY = 'users_live_on';
@Component({
  selector: 'app-users',
  imports: [AgGridAngular, CommonModule, FormsModule, NgxCsvParserModule, ClaimsEditorComponent, InlineScannerClaimsComponent, ChannelsEditorComponent, AccessEditorComponent, WelcomeEmailModalComponent],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss'
})
export class UsersComponent implements OnInit, OnDestroy {

  @ViewChild('usersFileImport') usersFileImport: any;

  subs: any = [];
  gridDarkTheme = agGridThemes.dark;
  roles: any[] = [];
  customers: any[] = [];
  // slug → brand_color from F2-ADMIN.Customers.brand_color. Driven by
  // get_customers(). Read by the Pool column's cellRenderer so each
  // per-customer pool's chip is tinted with the customer's brand
  // (matches the DNS-action HTML docs + helps the operator scan grids
  // that span pools). Legacy 'f2' falls back to a slate gray; unknown
  // slugs fall back to violet so they're visible until the doc gets a
  // color set.
  slugColors: Record<string, string> = {};
  grid: any;
  data: any[] = [];
  filtered_data: any[] = [];
  selected_user: any;
  theme: any;
  scanners: any[] = [];

  showAddUserModal = false;

  // IT-F2-407: split-button on Add User — main click opens the single-add
  // drawer above; chevron opens a small menu that routes to Bulk Add.
  add_user_menu_open = false;

  // IT-F2-407: Bulk Add Users modal — small textarea for pasting a list of
  // emails (one per line or comma-separated). On submit we normalize into
  // the same string[][] shape processImportUsers() feeds the CSV wizard,
  // so the downstream customer/scanners/dedup/branded-SES-banner path is
  // reused verbatim.
  bulk_add_modal_open = false;
  bulk_add_emails_text = '';
  openBulkAddMenu(ev: Event) {
    ev.stopPropagation();
    this.add_user_menu_open = !this.add_user_menu_open;
  }
  openBulkAddModal() {
    this.add_user_menu_open = false;
    this.bulk_add_emails_text = '';
    this.bulk_add_modal_open = true;
  }
  closeBulkAddModal() {
    this.bulk_add_modal_open = false;
    this.bulk_add_emails_text = '';
  }
  /** Split "pasted emails" (newline OR comma OR whitespace separated) into
   *  a de-duped, lower-cased email list. Trims each entry; drops anything
   *  that lacks '@'. */
  private _parseBulkAddEmails(raw: string): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const chunk of String(raw || '').split(/[\s,;]+/)) {
      const e = chunk.trim().toLowerCase();
      if (!e || !e.includes('@')) continue;
      if (seen.has(e)) continue;
      seen.add(e);
      out.push(e);
    }
    return out;
  }
  /** Route the pasted email list through the same CSV mapping wizard the
   *  file-import path uses. One email per row, no header, single "Email"
   *  column mapped automatically. Customer + scanners + correlated-claims
   *  + SES banner + pre-flight dedup are all inherited from the wizard. */
  submitBulkAddPasted() {
    const emails = this._parseBulkAddEmails(this.bulk_add_emails_text);
    if (emails.length === 0) {
      this.toastService.error('Bulk Add', 'Paste at least one email address (one per line or comma-separated).');
      return;
    }
    const raw: string[][] = emails.map(e => [e]);
    this.csvMapRawRows = raw;
    this.csvMapHasHeader = false;
    this.csvMapColumnMapping = this._auto_map_csv_columns(raw, false);
    // IT-F2-407 (Mike 2026-09-18 c/ac87641f): reset picker state on Bulk
    // Add entry — a sticky csvMapCustomer from an earlier CSV import
    // would silently route the paste-list to the wrong pool. Bulk Add is
    // always "you pick fresh" — force the operator to make an explicit
    // customer choice before Confirm can fire.
    this.csvMapCustomer = '';
    this.csvMapScanners = [];
    this.bulk_add_modal_open = false;
    this.bulk_add_emails_text = '';
    this.csvMapDialogOpen = true;
  }

  // IT-F2-106 Phase 1: right-slide drawer with tracker-style Maximize2
  // expand toggle. When true, drawer occupies the full viewport; when
  // false, drawer is a 460px right-anchored panel with dismissible
  // overlay. Mirrors the pattern from f2-tracker/src/App.tsx Drawer.
  addUserFullPage = false;
  toggleAddUserFullPage() { this.addUserFullPage = !this.addUserFullPage; }
  closeAddUserDrawer() {
    // Dirty-check: prompt if any of the primary create-user fields are
    // populated (name / email / claims / channels). Cheap heuristic;
    // matches the tracker's guardedClose intent without threading a
    // full draft-diff signal through the existing form.
    const dirty = !!(this.newUser?.name || this.newUser?.last_name ||
      this.newUser?.email || this.add_user_claims_json ||
      this.add_user_channels_json || this.selected_add_scanners?.length);
    if (dirty && !confirm('Discard unsaved changes to the new user?')) return;
    this.showAddUserModal = false;
    this.addUserFullPage = false;
  }

  // Post-create magic-link modal. Pops after Add-User succeeds against the
  // LEGACY pool — legacy has no branded SES sender configured, so the
  // backend refuses to auto-send the welcome email (stamps ER-4533V6F
  // shape: "Refusing to mint magic-link invite — would fall back to
  // Cognito default sender and look like phishing"). Instead of failing
  // the create, we skip the auto-send AND surface a small modal that
  // lets the admin mint the URL manually and paste to the user out-of-
  // band (email themselves, Slack, SMS). One row per created identity.
  showLegacyMagicLinkModal = false;
  pendingLegacyMagicLinks: Array<{
    email: string;
    username: string;
    pool_id?: string;
    pool_label: string;
    copied: boolean;
    busy: boolean;
  }> = [];
  newUser: any = {
    name: '',
    last_name: '',
    email: '',
    password: '',
    role: '',
    customers: '',
    scanners: '',
    // When true (default), backend creates the user with SUPPRESS and
    // sends a branded "set your password" magic-link email via the
    // pool's SES identity. When false, the legacy temp-password path
    // is used (Cognito sends its default invite with the temp pwd).
    // Only meaningful for per-customer pools — legacy pool falls back
    // to temp-password since it has no branded SES identity wired.
    send_magic_link: true,
  };
  // Add-User modal multi-select working state.
  // selected_add_scanners entries are scanner IDs OR wildcards like 'tt-*' (granting all scanners for a customer).
  selected_add_customers: string[] = [];
  selected_add_scanners: string[] = [];
  // Add-User modal: customer's welcome-email default lookup.
  // Populated by _apply_customer_welcome_default() whenever the selected
  // pool + customers change. Drives BOTH the initial send_magic_link
  // checkbox value AND the "Customer default: ON/OFF" hint under the
  // checkbox. null = not yet looked up (or legacy-pool → no lookup).
  add_user_customer_default_welcome: boolean | null = null;
  add_user_customer_default_source_slug = '';
  // Per-scanner claims + notify_channels JSON collected in the modal.
  // Set via the shared claims-editor / channels-editor components (same
  // UX the edit-user flow already uses). The text inputs on the form
  // are DISABLED — only the "Edit" button opens the modal that then
  // emits the JSON we stash here. Applied post-create via
  // updateUserAttributes on every pool the user landed in. Mike
  // 2026-07-02.
  // IT-F2-106 Phase 3d: per-scanner Claims entries — plain objects
  // keyed by scanner id. Owned as objects (not JSON strings) so the
  // new <app-inline-scanner-claims> component can mutate + emit via
  // (entryChange) without a stringify/parse round-trip per keystroke.
  // Parent stores the SAME object reference the child mutates; child
  // emits a shallow clone so parent's === compare works — Map.set
  // triggers re-render only when the reference actually changes.
  private _addUserPerScannerClaims = new Map<string, any>();
  // Aggregate JSON array consumed by on_create_user() at ~L2740.
  // Serializes entries with a valid scanner + at least one non-scanner
  // key (skips untouched entries) into the flat array the create flow
  // expects. Idempotent for empty state.
  get add_user_claims_json(): string {
    const out: any[] = [];
    for (const [sid, entry] of this._addUserPerScannerClaims) {
      if (!entry || typeof entry !== 'object') continue;
      const keys = Object.keys(entry).filter(k => k !== 'scanner');
      if (keys.length === 0) continue;  // template-defaults only; skip
      out.push({ ...entry, scanner: sid });
    }
    return out.length ? JSON.stringify(out) : '';
  }

  /** Fetches (or seeds) the entry object for a scanner. Called from
   *  the template — must return the SAME reference across CD ticks
   *  when there's no user action, otherwise the inline component's
   *  ngOnChanges fires spuriously. */
  entryForScanner(scannerId: string): any {
    let e = this._addUserPerScannerClaims.get(scannerId);
    if (!e) {
      e = {};
      this._addUserPerScannerClaims.set(scannerId, e);
    }
    return e;
  }

  /** (entryChange) handler from the inline component — receives a fresh
   *  shallow clone. Stash it in the Map replacing the prior reference. */
  onInlineClaimsEntryChange(scannerId: string, newEntry: any) {
    this._addUserPerScannerClaims.set(scannerId, newEntry);
  }

  /** Resolves the customer doc for a given scanner id via Scanner.Client.
   *  Used by the inline claims component to fall back to Customer.claims_schema. */
  customerForScanner(scannerId: string): any {
    const s = this._getScannerDoc(scannerId);
    const slug = String(s?.Client || '');
    if (!slug) return null;
    return (this.directoryCustomers || []).find((c: any) => String(c?._id || '') === slug) || null;
  }
  // IT-F2-106 Phase 3a: per-scanner channel picks — Set of channel ids
  // per scanner id, aggregated into `add_user_channels_json` on read
  // via the getter below. Replaces the standalone Channels row + modal
  // launcher (Mike: "combine enabling a scanner and the channels /
  // claims settings related to each scanner"). When a scanner with
  // channels[] is enabled, its sub-panel auto-expands (via
  // _addUserExpandedChannelsFor); when disabled, its picks are
  // dropped so a re-enable doesn't resurrect stale selections.
  private _addUserPerScannerChannels = new Map<string, Set<string>>();
  private _addUserExpandedChannelsFor = new Set<string>();
  // Aggregate CSV consumed by on_create_user() at ~line 2740.
  get add_user_channels_json(): string {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const picks of this._addUserPerScannerChannels.values()) {
      for (const id of picks) {
        if (!seen.has(id)) { seen.add(id); out.push(id); }
      }
    }
    return out.join(',');
  }
  // Mike 2026-07-01: pool-radio replaces the flat customer-checkbox
  // grid on the Add User modal. A single Cognito pool is picked first
  // (radio); the customer slug picker below scopes to that pool's
  // slugs (checkboxes for the legacy pool where multiple customers
  // share it, auto-selected for a dedicated pool that owns one
  // customer). Empty string = legacy pool sentinel. null = not picked
  // yet (submit blocked); the two states are distinct so can_create_user
  // can tell "picked legacy" from "hasn't touched the radio group".
  add_selected_pool_id: string | null = null;
  // Pre-flight: pool_ids in which the newUser.email already has an
  // identity (Redis F2:USER_INDEX_BY_EMAIL:<email>). Populated when
  // the admin types a valid email. Customers whose resolved pool_id
  // is in this set get a disabled checkbox in the picker so the admin
  // can't try to create a duplicate identity. Mike 2026-06-26.
  add_email_existing_pool_ids: string[] = [];
  add_email_lookup_busy = false;
  private _add_email_lookup_timer: any = null;
  showDeleteUserModal = false;
  showEditUserScannersModal = false;
  showEditUserCustomersModal = false;
  // Toolbar Actions dropdown open/close. Two items: Override Password
  // (opens the password-set modal) and Send Welcome Email (opens the
  // dedicated welcome-email preview+send modal, NOT the override-password
  // magic_link mode — that was a separate admin bulk-password flow).
  // Closed on outside click via the transparent backdrop in the template.
  actions_open = false;
  // Welcome-email modal state. Users selected via checkbox; on open,
  // the modal's ngOnChanges fetches /rest/welcome-email/preview and
  // renders A/B samples using the first user's data.
  showWelcomeEmailModal = false;
  welcomeEmailUsers: WelcomeEmailUserInput[] = [];
  // Multi-select bulk action: override password.
  // selected_users_for_password is populated from the grid's
  // onSelectionChanged when the rowSelection switches to multiRow.
  showOverridePasswordModal = false;
  selected_users_for_password: any[] = [];
  override_password_value = '';
  override_password_temporary = true;
  override_password_busy = false;
  // Live progress for the multi-user bulk override. Painted on every
  // iteration via setTimeout(0) yield so Angular's CD ticks and the
  // operator sees the bar advance. Cancel flips `cancelled` and the
  // loop bails on the next iteration boundary.
  override_password_progress: {
    total: number;
    done: number;
    ok: number;
    fail: number;
    cancelled: boolean;
    current_user: string;
  } = { total: 0, done: 0, ok: 0, fail: 0, cancelled: false, current_user: '' };
  // Impersonate (view-as) modal state. Distinct from multi-select
  // password override -- a single target only, since the destination
  // URL is per-user (their customer host or one of their entitled
  // scanners).
  showImpersonateModal = false;
  impersonate_target: any = null;
  impersonate_kind: 'members' | 'scanner' = 'members';
  impersonate_scanner_id = '';
  impersonate_busy = false;
  // Result panel state shown AFTER a successful mint -- keeps the
  // generated URL visible with a Copy button so admin can paste it
  // into a support ticket / Slack thread when troubleshooting.
  impersonate_result_url = '';
  impersonate_result_target_email = '';
  // Human-friendly "First Last <email>" — set from backend a700286's
  // target_display response field. Falls back to email when the target
  // has no given_name/family_name set.
  impersonate_result_target_display = '';
  impersonate_result_expires_at = '';
  selected_user_scanners: any[] = [];
  selected_user_customers: any[] = [];
  // Claims editor modal state. The component is reusable
  // (shared/claims-editor) — these props just bridge it to the grid.
  // The editor reads `directoryCustomers` (full Customer docs with the
  // claims_schema metadata embedded — fetched from CustomerDirectoryService,
  // NOT the legacy slug-array `customers` field above) and `this.scanners`.
  showEditUserClaimsModal = false;
  selected_user_for_claims: any = null;
  selected_user_claims_json: string = '';
  directoryCustomers: any[] = [];
  // Unified scanner-access editor (Mike 2026-06-28). Single dialog
  // drives both custom:customers + custom:scanners — co-dependent
  // attributes that drifted when edited via two separate modals.
  showEditUserAccessModal = false;
  selected_user_for_access: any = null;
  // Channels editor modal state — sibling of claims editor, edits
  // `custom:notify_channels` driven by F2-ADMIN.Customers.channels_schema.
  showEditUserChannelsModal = false;
  selected_user_for_channels: any = null;
  selected_user_channels_json: string = '';

  constructor(private authService: AuthService,
    private adminService: UsersService,
    private toastService: ToastService,
    private ngxCsvParser: NgxCsvParser,
    private scannersService: ScannersService,
    private customerDirectoryService: CustomerDirectoryService,
    private route: ActivatedRoute,
    public syncState: SyncStateService,  // public — used in the Refresh Cache button template binding
    private welcomeEmailService: WelcomeEmailService,  // IT-F2-407 c/4e907c83 — inline welcome email on bulk-add entitlement
  ) {
    this.init_grid();

    this.theme = this.gridDarkTheme;

    // When ANY operator's sync finishes (started here OR in another tab),
    // refresh this grid so it reflects the post-sync state. Service emits
    // onSyncFinished$ on every started→finished transition.
    this.syncState.onSyncFinished$.subscribe((ts) => {
      if (ts > 0) this.refresh();
    });
  }

  // Inbound deep-link filter (e.g. /admin/users?client=option-pit&status=active
  // from the Customer Directory # Clients hyperlinks, OR
  // /admin/users?email=<email> from the error-reports right-click
  // "Look up in Users panel" action). Applied in on_grid_ready once the
  // grid api is available.
  pendingClientFilter: string | null = null;
  pendingStatusFilter: 'active' | 'inactive' | null = null;
  pendingEmailFilter: string | null = null;

  // Multi-email deep-link (?emails=a@x.com,b@y.com). Set when the operator
  // right-clicks selected rows in the Commerce Events grid and picks
  // "Look up N users in Users panel". ag-grid's agTextColumnFilter caps
  // at 2 OR-conditions so we route this through the External Filter API
  // instead, keyed on a lower-cased email Set. Rendered as a dismissable
  // chip above the grid so the operator can see the scope + clear it in
  // one click. Mike 2026-07-05.
  emailListFilter: Set<string> | null = null;
  emailListRaw: string[] = [];

  async ngOnInit() {
    const q = this.route.snapshot.queryParamMap;
    this.pendingClientFilter = q.get('client');
    const status = q.get('status');
    if (status === 'active' || status === 'inactive') this.pendingStatusFilter = status;
    this.pendingEmailFilter = q.get('email');
    // Multi-email deep-link from Commerce Events context menu (or hand-
    // typed): ?emails=a,b,c. Empty entries + dupes stripped, all values
    // lowercased so match is case-insensitive (Cognito is case-preserving
    // but lookup is case-insensitive across the fleet).
    const emailsRaw = q.get('emails');
    if (emailsRaw) {
      const list = String(emailsRaw)
        .split(',')
        .map(s => s.trim().toLowerCase())
        .filter(Boolean);
      if (list.length) {
        this.emailListRaw = Array.from(new Set(list));
        this.emailListFilter = new Set(this.emailListRaw);
      }
    }
    // Restore Live-on preference. Default ON (treats missing key as on)
    // so a fresh operator gets live updates without opting in.
    try {
      const stored = localStorage.getItem(LIVE_LOCALSTORAGE_KEY);
      this.liveOn = stored == null ? true : stored !== '0';
    } catch { /* localStorage unavailable — leave default ON */ }
    this.get_roles();
    await this.get_scanners();
    await this.get_customers();
    // Full Customer docs (with claims_schema embedded) for the
    // ClaimsEditor. The legacy `customers` slug array stays as-is so
    // the existing edit-customers modal isn't disturbed.
    this._load_directory_customers();
    await this.get_users();
  }

  /**
   * Per-row tint based on the customer's brand_color, derived from the
   * row's `customer_slug` stamp. Alternates 6% / 3% opacity row-to-row
   * (Mike 2026-06-26) so eye-tracking gets the same help zebra-striping
   * gives, but in-brand. Falls back to slate gray for legacy ('f2')
   * slug rows so they get a visible-but-neutral tint instead of staying
   * transparent.
   */
  private _row_tint_style(data: any, rowIndex: number): { backgroundColor?: string } | undefined {
    const slug = String(data?.customer_slug || '').trim();
    if (!slug) return undefined;
    const color = this.slugColors[slug] || (slug === 'f2' ? '#94a3b8' : '');
    if (!color) return undefined;
    const opacityHex = rowIndex % 2 === 0 ? '10' : '08';
    return { backgroundColor: `${color}${opacityHex}` };
  }

  private async _load_directory_customers() {
    try {
      const res: any = await this.customerDirectoryService.list();
      this.directoryCustomers = Array.isArray(res) ? res : [];
    } catch (e) {
      console.warn('customer-directory list failed (claims editor will be schema-less)', e);
      this.directoryCustomers = [];
    }
    // Build slug→brand_color map for the Pool column's cellRenderer.
    // get_customers() (the /admin/customers endpoint) only returns
    // distinct scanner-client slugs as STRINGS, not Customer docs —
    // brand_color lives on the doc, so we have to source it from the
    // directory list (full Customer docs minus secrets).
    const next: Record<string, string> = {};
    for (const c of this.directoryCustomers || []) {
      const slug = String((c as any)?._id || '').trim();
      const color = String((c as any)?.brand_color || '').trim();
      if (slug && color) next[slug] = color;
    }
    this.slugColors = next;
    if (this.grid?.api) {
      this.grid.api.refreshCells({ columns: ['customer_slug'], force: true });
      // getRowStyle reads slugColors → row tint. Redraw so the tint
      // applies the moment the directory load lands (before the load,
      // getRowStyle returns undefined and rows render unstyled).
      if (this.grid.api.redrawRows) this.grid.api.redrawRows();
    }
  }

  // Apply the deep-link filters to the grid. text-filter on
  // attributes.custom:customers (contains <slug>) + on enabled
  // (equals true/false). Re-applied both at grid-ready and after
  // get_users() lands rows.
  apply_deep_link_filters() {
    if (!this.grid?.api) return;
    // Apply filters per column with setColumnFilterModel (Promise-based,
    // ag-grid v33+). attributes.custom:customers has filter:
    // 'agTextColumnFilter' explicitly; `enabled` inherits the Enterprise
    // default which is agSetColumnFilter — its model is {filterType:'set',
    // values:[...]} with stringified cell values.
    const tasks: Promise<any>[] = [];
    if (this.pendingClientFilter) {
      tasks.push(Promise.resolve(
        this.grid.api.setColumnFilterModel('attributes.custom:customers', {
          filterType: 'text', type: 'contains', filter: this.pendingClientFilter,
        })
      ));
    }
    if (this.pendingStatusFilter) {
      tasks.push(Promise.resolve(
        this.grid.api.setColumnFilterModel('enabled', {
          filterType: 'set',
          values: [this.pendingStatusFilter === 'active' ? 'true' : 'false'],
        })
      ));
    }
    if (this.pendingEmailFilter) {
      // Email column now uses agTextColumnFilter (see colDef) so the
      // floating filter input visibly shows the typed/deep-linked
      // value. Contains-match is fine semantically — the deep-link
      // value is a full email and the operator can still narrow
      // further by editing the input.
      tasks.push(Promise.resolve(
        this.grid.api.setColumnFilterModel('attributes.email', {
          filterType: 'text',
          type: 'contains',
          filter: this.pendingEmailFilter,
        })
      ));
    }
    if (!tasks.length) return;
    Promise.all(tasks)
      .then(() => this.grid.api.onFilterChanged())
      .catch((e: any) => console.warn('apply_deep_link_filters failed', e));
  }
  // ── Live updates ────────────────────────────────────────────────────
  // EventSource against /admin/users-live/stream — refetches the affected
  // row on each {pool_id, username, op} envelope and patches the grid
  // in place via applyTransaction. Cross-origin EventSource cookie
  // delivery is unreliable across browsers, so auth is a 60s HMAC
  // bearer minted via /users-live/stream-token (cookie-authed) and passed
  // on the query string. Mirrors error-reports component.
  //
  // Toggled by the "Live" pill at the bottom-right of the grid status
  // bar; persistent across reloads via localStorage. Defaults ON so
  // grid stays in lockstep with Cognito edits from elsewhere by default.
  liveOn = true;
  private _userEventSource: EventSource | null = null;
  private _userSseReconnectTimer: any = null;
  // Debounce coalescing for bursty rewrites (full sync iterating 17k
  // users would otherwise fire 17k refetches). Map<rowId, timer>.
  // NOTE: the per-row debounce only dedupes the SAME row, not the storm
  // of 17k DISTINCT rows from a full-sync reconcile. The burst-breaker
  // below is the defense for that case; this debounce stays as protection
  // for legitimate single-row rapid edits.
  private _userSseDebounce: Map<string, any> = new Map();
  // Burst-breaker: when more events arrive than humanly plausible
  // (default >50 events / 2s), stop dispatching to the per-row debouncer
  // entirely and queue a single full refresh after the storm subsides.
  // Pairs with the server-side suppress (f2-admin-service 8fcd2d7) which
  // closes the storm at its source for the /sync-all path; the breaker
  // catches any other future storm path we haven't enumerated yet.
  private _sseBurstBreaker = new SseBurstBreaker({
    onPostStormRefresh: () => this.refresh(),
  });
  // Skip events with usernames we just refetched within 250ms — the
  // backend cache_cognito_users iterating publishes one event per HSET
  // even when the row contents didn't change.
  private _userSseLastFetchAt: Map<string, number> = new Map();

  ngOnDestroy(): void {
    this._close_user_event_source();
    for (const t of this._userSseDebounce.values()) clearTimeout(t);
    this._userSseDebounce.clear();
    this._sseBurstBreaker.dispose();
  }

  /**
   * Open the live-users EventSource. Called from on_grid_ready once
   * the grid api is available — events arriving before grid-ready
   * would have nowhere to land.
   */
  private async _open_user_event_source() {
    if (!this.liveOn) return;
    if (this._userEventSource) return;
    if (this._userSseReconnectTimer) return;
    const token = await this.adminService.mintUsersStreamToken();
    if (!token) {
      this._schedule_user_sse_reconnect();
      return;
    }
    try {
      const url = this.adminService.getUsersStreamUrl(token);
      const es = new EventSource(url);
      this._userEventSource = es;
      es.onmessage = (ev: MessageEvent) => {
        if (!ev?.data) return;
        // Burst-breaker — drop incoming events when the inbound rate
        // exceeds threshold; queue ONE full refresh after the storm.
        // Protects against the 17k-event reconcile storm even if a
        // future code path stops respecting the server-side suppress.
        if (this._sseBurstBreaker.shouldDrop()) return;
        try {
          const evt = JSON.parse(ev.data);
          if (!evt?.pool_id || !evt?.username) return;
          this._enqueue_user_sse_event(evt.pool_id, evt.username, evt.op, evt.customer_slug);
        } catch { /* malformed */ }
      };
      es.onerror = () => {
        console.warn('users stream error; re-minting token + reopening');
        this._close_user_event_source();
        this._schedule_user_sse_reconnect();
      };
    } catch (e: any) {
      console.warn('users stream open failed:', e?.message);
      this._userEventSource = null;
      this._schedule_user_sse_reconnect();
    }
  }

  private _schedule_user_sse_reconnect() {
    if (!this.liveOn) return;
    if (this._userSseReconnectTimer) return;
    this._userSseReconnectTimer = setTimeout(() => {
      this._userSseReconnectTimer = null;
      if (this.liveOn) this._open_user_event_source();
    }, 3000);
  }

  /**
   * Toggle live-updates on/off, persist preference, open or close the
   * EventSource accordingly. Bound to the "Live" pill at the bottom-
   * right of the grid status bar.
   */
  toggleLive() {
    this.liveOn = !this.liveOn;
    try { localStorage.setItem(LIVE_LOCALSTORAGE_KEY, this.liveOn ? '1' : '0'); } catch { /* ignore */ }
    if (this.liveOn) {
      this._open_user_event_source();
    } else {
      this._close_user_event_source();
    }
  }

  private _close_user_event_source() {
    if (this._userEventSource) {
      try { this._userEventSource.close(); } catch { /* ignore */ }
      this._userEventSource = null;
    }
    if (this._userSseReconnectTimer) {
      clearTimeout(this._userSseReconnectTimer);
      this._userSseReconnectTimer = null;
    }
  }

  /**
   * Debounce + dedupe SSE events keyed by compound rowId. A full-sync
   * publish flood (17k+ events in seconds) would otherwise hammer the
   * cached-read endpoint. We coalesce to one refetch per row per
   * 200ms window, which still feels instant to a human watching one
   * specific row change.
   */
  private _enqueue_user_sse_event(pool_id: string, username: string, op?: string, customer_slug?: string) {
    const rowId = `${pool_id}::${username}`;
    const existing = this._userSseDebounce.get(rowId);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      this._userSseDebounce.delete(rowId);
      this._apply_user_sse_event(pool_id, username, op, customer_slug).catch(() => {});
    }, 200);
    this._userSseDebounce.set(rowId, timer);
  }

  private async _apply_user_sse_event(pool_id: string, username: string, op?: string, customer_slug?: string) {
    const api = this.grid?.api;
    if (!api) return;
    const rowId = `${pool_id}::${username}`;
    if (op === 'delete') {
      const node = api.getRowNode(rowId);
      if (node?.data) {
        api.applyTransaction({ remove: [node.data] });
        _.remove(this.data, (u: any) => u.pool_id === pool_id && u.username === username);
      }
      return;
    }
    const row = await this.adminService.getCachedUser(username, pool_id);
    if (!row) return;
    // Backend `get_cached_users` doesn't always re-stamp pool_id /
    // customer_slug on the single-row read (it was originally written
    // to flag rows in the cross-pool list view). Stamp them ourselves
    // so getRowId matches and the Pool column renders the slug instead
    // of '----'. customer_slug from the SSE event payload is the
    // authoritative source (publisher parses first entry of
    // custom:customers from the cached payload).
    if (!row.pool_id) row.pool_id = pool_id;
    if (!row.customer_slug && customer_slug) row.customer_slug = customer_slug;
    const node = api.getRowNode(rowId);
    if (node) {
      api.applyTransaction({ update: [row] });
      const idx = this.data.findIndex((u: any) => u.pool_id === pool_id && u.username === username);
      if (idx >= 0) this.data[idx] = row;
    } else {
      api.applyTransaction({ add: [row], addIndex: 0 });
      this.data = [row, ...this.data];
    }
  }

  async get_roles() {
    let res = await this.adminService.getRoles();
    if (!res?.err) {
      this.roles = res;
    }
    else {
      this.roles = [];
    }
  }
  async get_customers() {
    let res = await this.adminService.getCustomers();
    if (!res?.err) {
      this.customers = res;
    }
    else {
      this.customers = [];
    }
    // brand_color sourcing lives on _load_directory_customers() — this
    // endpoint returns slug-strings only, no doc fields.
  }
  async get_users() {
    let res = await this.adminService.getUsers();
    localLog('get_users() =>', res);
    if (res && !res.err) {
      this.data = res;
    }
    else {
      this.data = [];
    }
    // Re-apply deep-link filters now that rowData has landed — the
    // initial grid-ready apply is a no-op on empty grids.
    if (this.pendingClientFilter || this.pendingStatusFilter || this.pendingEmailFilter) {
      setTimeout(() => this.apply_deep_link_filters(), 0);
    }
  }
  async get_scanners() {
    this.scanners = [];
    let res = await this.scannersService.getScanners();
    if (res && !res.err) {
      this.scanners = res;
    }
  }
  async refresh() {
    this.selected_user = null;
    await this.get_roles();
    await this.get_users();
  }

  // Force-refresh the Redis user cache from Cognito and re-load the grid.
  // Use when out-of-band Cognito edits (AWS console, batch scripts) need to
  // be reflected in the admin UI without waiting for the next periodic sync.
  // Long-running (~30s for ~17k users).
  //
  // Cross-tab coordination: the backend acquires a Redis lock on
  // F2:USER_SYNC_LOCK and publishes 'started'/'finished' sync_state events
  // on F2:USER_UPDATES. SyncStateService consumes those events and drives
  // the global SyncOverlayComponent + this button's disabled state. If
  // another operator is mid-sync, the backend returns {in_progress: true}
  // and we just nudge the SyncStateService to confirm its overlay is up
  // rather than firing a toast.
  async refresh_user_cache() {
    let res: any = await this.adminService.syncAllUsers();
    if (res && res.in_progress) {
      // Lock already held by another operator — overlay should already be
      // showing via SSE; nudge the service to verify state from the source
      // of truth in case the SSE event was missed.
      await this.syncState.refreshFromServer();
      return;
    }
    if (res && !res.err) {
      // Grid will also auto-refresh via onSyncFinished$ — this extra call
      // is the legacy path for if the SSE missed for any reason.
      await this.refresh();
    }
  }
  async reset_user_password(user: any) {
    let res = await this.adminService.resetPassword(user.username);
  }
  async clear_filters() {
    // Clear grid filters
    if (this.grid.api) {
      this.grid.api.setFilterModel(null);
    }
  }

  /**
   * Copy a shareable URL to clipboard that reproduces the current grid
   * filter + sort + external-email filter state. Snapshots into ONE
   * base64-encoded JSON query param `?filters=<b64>` so a paste target
   * lands with the exact same view. Uses base64url encoding so the
   * result is copy-paste safe (no `+`, `/`, `=` padding issues in URLs).
   * Mike 2026-07-06.
   */
  shareFilteredLink(): void {
    if (!this.grid?.api) return;
    const model: any = {};
    try { model.filter = this.grid.api.getFilterModel() || {}; } catch { model.filter = {}; }
    try {
      // Keep only columns with an explicit sort — the full column state
      // snapshot would balloon the URL by ~4KB for the ~30 columns.
      const cs = this.grid.api.getColumnState?.() || [];
      const sortState = cs
        .filter((c: any) => c.sort)
        .map((c: any) => ({ colId: c.colId, sort: c.sort, sortIndex: c.sortIndex }));
      if (sortState.length) model.sort = sortState;
    } catch { /* ignore */ }
    if (this.emailListFilter && this.emailListFilter.size > 0) {
      model.emails = Array.from(this.emailListFilter);
    }
    let b64 = '';
    try {
      const json = JSON.stringify(model);
      // base64url: '+' → '-', '/' → '_', strip '=' padding — safe in query strings.
      b64 = btoa(json).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } catch (e: any) {
      this.toastService.error('Share filter', 'Failed to encode filter state: ' + String(e?.message || e));
      return;
    }
    const base = `${window.location.origin}${window.location.pathname}`;
    const url = `${base}?filters=${b64}`;
    // navigator.clipboard needs a secure context. admin.f2-tech.ai is
    // https so this always resolves; fall back to an <input>+execCommand
    // sequence just in case the browser refuses (Firefox in some
    // extension-controlled tabs).
    const write = navigator.clipboard?.writeText;
    if (typeof write === 'function') {
      navigator.clipboard.writeText(url)
        .then(() => this.toastService.success('Link copied', 'Filtered view URL is on your clipboard.'))
        .catch(() => this._fallbackCopy(url));
    } else {
      this._fallbackCopy(url);
    }
  }

  /**
   * execCommand('copy') fallback for browsers that refuse the async
   * clipboard API. Creates a hidden <input>, selects, copies, removes.
   */
  private _fallbackCopy(url: string): void {
    try {
      const el = document.createElement('input');
      el.value = url;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      this.toastService.success('Link copied', 'Filtered view URL is on your clipboard.');
    } catch (e: any) {
      this.toastService.error('Share filter', 'Copy blocked by browser. URL: ' + url);
    }
  }

  /**
   * Restore filter + sort + external-email state from a `?filters=<b64>`
   * query param. Called from apply_deep_link_filters after the initial
   * pending* params are handled — the two paths compose, so a caller
   * can bookmark a URL like `?filters=<b64>` OR the legacy shape
   * `?email=<x>&client=<y>`. Silent on decode errors; the panel just
   * lands on its default state.
   */
  private _apply_filters_query_param(): void {
    if (!this.grid?.api) return;
    const raw = this.route.snapshot.queryParamMap.get('filters');
    if (!raw) return;
    let json: string;
    try {
      // Reverse base64url encoding.
      const b64 = raw.replace(/-/g, '+').replace(/_/g, '/');
      const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
      json = atob(b64 + pad);
    } catch {
      return;
    }
    let model: any;
    try { model = JSON.parse(json); } catch { return; }
    if (model?.filter && typeof model.filter === 'object') {
      try { this.grid.api.setFilterModel(model.filter); } catch { /* ignore */ }
    }
    if (Array.isArray(model?.sort) && model.sort.length) {
      try { this.grid.api.applyColumnState({ state: model.sort, defaultState: { sort: null } }); } catch { /* ignore */ }
    }
    if (Array.isArray(model?.emails) && model.emails.length) {
      const set = new Set<string>(model.emails.map((s: any) => String(s || '').trim().toLowerCase()).filter(Boolean));
      if (set.size) {
        this.emailListRaw = Array.from(set);
        this.emailListFilter = set;
      }
    }
    try { this.grid.api.onFilterChanged(); } catch { /* ignore */ }
  }

  /**
   * Export the currently-filtered-and-sorted grid rows as CSV, minus any
   * columns that don't make sense in a spreadsheet (checkboxes, the New
   * pill, the Edit affordance). ag-grid Enterprise's exportDataAsCsv
   * honors the current sort order so the file matches what the operator
   * sees on screen. Filename carries a timestamp for easy versioning.
   * Mike 2026-07-06.
   */
  /** IT-F2-428 (Mike 2026-09-25): open the per-customer reconciliation
   *  report modal. Popup shows a customer picker + short explainer; on
   *  export it filters this.data by (customer_slug OR custom:customers
   *  contains slug) and downloads a CSV with the exact fields Mike
   *  listed: email / status / scanners / claims / created / last login /
   *  last updated (Cognito last-modified — proxies deactivation time). */
  show_reconciliation_modal = false;
  reconciliation_customer = '';
  openReconciliationModal(): void {
    this.reconciliation_customer = '';
    this.show_reconciliation_modal = true;
  }
  closeReconciliationModal(): void {
    this.show_reconciliation_modal = false;
  }
  exportReconciliationCsv(): void {
    const cust = String(this.reconciliation_customer || '').trim();
    if (!cust) {
      this.toastService.error('Reconciliation report', 'Pick a customer first.');
      return;
    }
    const cust_lower = cust.toLowerCase();
    const rows = (this.data || []).filter((u: any) => {
      if (String(u?.customer_slug || '').toLowerCase() === cust_lower) return true;
      const list = String(u?.attributes?.['custom:customers'] || '').toLowerCase();
      return list.split(',').map((s: string) => s.trim()).filter(Boolean).includes(cust_lower);
    });
    if (!rows.length) {
      this.toastService.info('Reconciliation report', `No users on file for ${cust}.`);
      return;
    }
    const status_label = (s: any): string => {
      switch (String(s || '').toUpperCase()) {
        case 'CONFIRMED': return 'Active';
        case 'FORCE_CHANGE_PASSWORD': return 'Force Change Password';
        case 'DISABLED': return 'Disabled';
        case 'UNCONFIRMED': return 'Unconfirmed';
        case 'RESET_REQUIRED': return 'Reset Required';
        default: return String(s || '');
      }
    };
    const fmt_et = (iso: any): string => {
      if (!iso) return '';
      const d = DateTime.fromISO(String(iso), { zone: 'utc' }).setZone('America/New_York');
      return d.isValid ? d.toFormat('yyyy-MM-dd HH:mm') : '';
    };
    const csv_esc = (v: any): string => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ['Email', 'Name', 'Status', 'Scanners', 'Claims', 'Created (ET)', 'Last Login (ET)', 'Last Updated (ET)'];
    const lines = [header.join(',')];
    for (const u of rows) {
      const attrs = u?.attributes || {};
      const first = String(attrs['given_name'] || attrs['name'] || '').trim();
      const last  = String(attrs['family_name'] || '').trim();
      const name = [first, last].filter(Boolean).join(' ');
      lines.push([
        csv_esc(attrs['email'] || u?.username || ''),
        csv_esc(name),
        csv_esc(status_label(u?.status)),
        csv_esc(attrs['custom:scanners'] || ''),
        csv_esc(attrs['custom:claims'] || ''),
        csv_esc(fmt_et(u?.created_at)),
        csv_esc(fmt_et(attrs['custom:last_login'])),
        csv_esc(fmt_et(u?.updated_at || attrs['updated_at'])),
      ].join(','));
    }
    const stamp = DateTime.now().toFormat('yyyy-MM-dd HH-mm');
    const filename = `reconciliation-${cust}-${stamp}.csv`;
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    this.toastService.success('Reconciliation report', `${rows.length} user${rows.length === 1 ? '' : 's'} exported for ${cust}.`);
    this.closeReconciliationModal();
  }

  exportFilteredCsv(): void {
    if (!this.grid?.api?.exportDataAsCsv) {
      this.toastService.error('CSV export', 'ag-grid API not ready yet — try again in a moment.');
      return;
    }
    // Skip the ag-grid-internal columns (selection checkboxes) plus any
    // renderer-only columns that would either encode as [object Object]
    // or dump long HTML into the CSV.
    let visibleCols: string[] = [];
    try {
      const cs = this.grid.api.getColumnState?.() || [];
      visibleCols = cs
        .filter((c: any) => !c.hide)
        .map((c: any) => c.colId)
        .filter((id: string) => id && !id.startsWith('ag-Grid-') && id !== '__new__' && id !== '__edit__');
    } catch { /* fall through to default column set */ }
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    try {
      this.grid.api.exportDataAsCsv({
        fileName: `f2-users-filtered-${stamp}.csv`,
        // filteredAndSorted matches what the operator sees on screen —
        // ignores the raw rowData order and any hidden rows.
        exportedRows: 'filteredAndSorted',
        ...(visibleCols.length ? { columnKeys: visibleCols } : {}),
      });
      this.toastService.success('CSV export', 'Downloaded the currently-filtered rows.');
    } catch (e: any) {
      this.toastService.error('CSV export', String(e?.message || e));
    }
  }


  /**** Users Grid ****/
  init_grid() {
  
    this.grid = {
      options: {
        context: this,
        suppressCellFocus: true,
        // multiRow + checkboxes so the admin can pick a batch of users
        // and run "Override password" once. enableClickSelection stays
        // true so single-row workflows (open edit modal on row click)
        // are unaffected -- the on_row_clicked handler still fires.
        //
        // selectAll: 'filtered' scopes the header checkbox to only the
        // currently filtered rows -- ag-grid's default of 'all' silently
        // selected every row in the grid (17k+) even when the operator
        // had filtered down to <1k, which is the bug Mike hit.
        rowSelection: {
          mode: 'multiRow',
          checkboxes: true,
          headerCheckbox: true,
          selectAll: 'filtered',
          enableClickSelection: true,
        },
        defaultColDef: {
          floatingFilter: true,
          // filter: 'agTextColumnFilter',
          filter: true,
          cellStyle: { 'text-align': 'left'},
        },
        getRowId: (params: any) => {
          // Compound key — post-Workstream-A, the same Cognito Username
          // (UUID for legacy, email for per-customer pools seeded via
          // AdminCreateUser) can appear in multiple pools. Username alone
          // would collide and ag-grid would silently dedupe rows. Pool +
          // username uniquely identifies a Cognito identity.
          const pool = params.data?.pool_id || 'unknown';
          return `${pool}::${params.data.username}`;
        },
        // Slight customer-color tint on each row — same color source as
        // the Pool pill, ~6% opacity hex suffix. Lets the admin scan a
        // cross-pool grid and see which row belongs to which customer
        // at a glance. Per Mike 2026-06-26.
        getRowStyle: (params: any) => this._row_tint_style(params?.data, params?.node?.rowIndex || 0),
        onRowClicked: this.on_row_clicked.bind(this),
        onSelectionChanged: this.on_selection_changed.bind(this),
        // External filter — used ONLY by the multi-email deep-link from
        // Commerce Events (?emails=a,b,c). Ag-grid's agTextColumnFilter
        // caps at 2 OR-conditions so we can't stuff N emails through
        // setColumnFilterModel. isExternalFilterPresent returns true iff
        // the Set is populated; doesExternalFilterPass answers per-row
        // by comparing attributes.email against the Set case-insensitively.
        // Composes cleanly with any additional column filters the operator
        // adds — ag-grid AND's external with column filters.
        isExternalFilterPresent: () => !!(this.emailListFilter && this.emailListFilter.size > 0),
        doesExternalFilterPass: (node: any) => {
          if (!this.emailListFilter) return true;
          const em = String(node?.data?.attributes?.email || '').trim().toLowerCase();
          return em ? this.emailListFilter.has(em) : false;
        },
      },
      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalRowCountComponent', align: 'left' },
          { statusPanel: 'agFilteredRowCountComponent', align: 'left' },
        ]
      },
      columnDefs: [
        {
          field: "enabled",
          headerName: 'Enabled',
          width: 95,
          suppressSizeToFit: true,
          cellStyle: { 'text-align': 'center' },
          cellRenderer: (params: any) => {
            if (params.value === true) {
              return '<i class="fa fa-circle-check text-green-700"></i>';
            }
            else if (params.value === false) {
              return '<i class="fa fa-circle-xmark text-red-800"></i>';
            }
            else {
              return '?';
            }
          },
          editable: true,
          cellEditorSelector: (params: any) => {
            return {
              component: 'agSelectCellEditor',
              params: {
                values: [true, false]
              }
            }
          },
          onCellValueChanged: this.on_user_edit.bind(this),
        },
        {
          field: "username",
          headerName: 'Username',
          width: 290,
          suppressSizeToFit: true,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: "attributes.name",
          headerName: 'First Name',
          minWidth: 112,
          floatingFilterComponent: SetFloatingFilter,
          editable: true,
          onCellValueChanged: this.on_user_edit.bind(this),
        },
        {
          field: "attributes.family_name",
          headerName: 'Last Name',
          minWidth: 112,
          floatingFilterComponent: SetFloatingFilter,
          editable: true,
          onCellValueChanged: this.on_user_edit.bind(this),
        },
        {
          // Cognito pool the row came from. Same email can appear in N
          // pools post-Workstream-A (legacy + every per-customer pool);
          // backend get_users_across_pools stamps customer_slug and pool_id
          // on each row so the admin can tell duplicates apart. 'f2' = legacy
          // pool (admin-spanning identities live here); a customer slug like
          // 'option-pit' or 'qpp' = per-customer pool entry.
          field: "customer_slug",
          headerName: 'Pool',
          width: 130,
          suppressSizeToFit: true,
          floatingFilterComponent: SetFloatingFilter,
          cellRenderer: (params: any) => {
            const slug = params?.value;
            if (!slug) return '<span class="text-gray-400">—</span>';
            // IT-F2-421 (Mike 2026-09-23 chat): differentiate the two F2
            // pools. Both f2-dev-pool + f2-users-pool stamp customer_slug='f2'
            // on their rows, so labelling by slug alone (previous: always
            // "F2 (legacy)") mashes them together. Use the row's pool_id
            // to split them: legacy env pool id → "dev (legacy)";
            // F2 customer's own pool → "f2".
            const rowPoolId = String(params?.data?.pool_id || '').trim();
            const f2Cust = (this.directoryCustomers || []).find((c: any) => String(c?._id || '') === 'f2');
            const f2OwnPoolId = String(f2Cust?.cognito_pool?.user_pool_id || '').trim();
            let label: string;
            let isLegacy = false;
            if (slug === 'f2') {
              if (rowPoolId && f2OwnPoolId && rowPoolId === f2OwnPoolId) {
                label = 'f2';
              } else {
                label = 'dev (legacy)';
                isLegacy = true;
              }
            } else {
              label = slug;
            }
            // Brand color from F2-ADMIN.Customers.brand_color when set,
            // otherwise a safe per-class default (slate for legacy,
            // violet for unknown per-customer pools so it stands out
            // until the doc gets a color).
            const color = this.slugColors[slug] || (isLegacy ? '#94a3b8' : '#7c3aed');
            // Inline style — Tailwind can't pre-compile arbitrary hex.
            // 1a = ~10% opacity bg tint; 33 = ~20% border tint; full
            // hex on the text for contrast on dark theme.
            const style = `background:${color}1a;color:${color};border:1px solid ${color}33`;
            return `<span class="px-1.5 py-0.5 rounded text-[10px] font-mono" style="${style}">${label}</span>`;
          },
        },
        {
          // Cognito user_status — surfaces FORCE_CHANGE_PASSWORD and
          // related states so an admin can spot users who can't use
          // forgot-password before sending them down that dead-end.
          // Backend's auto-fallback (commit 1203a15) reroutes
          // FORCE_CHANGE_PASSWORD forgot-password requests to magic-
          // link, but having the state visible in the grid lets an
          // admin send the magic-link proactively. Mirrored on
          // f2-clients per Mike 2026-06-26.
          field: "status",
          headerName: 'Status',
          width: 145,
          suppressSizeToFit: true,
          floatingFilterComponent: SetFloatingFilter,
          cellRenderer: (params: any) => {
            const s = String(params?.value || '').trim();
            if (!s) return '<span class="text-gray-400">—</span>';
            // Color the pill by severity:
            //   green:  CONFIRMED (active, can sign in normally)
            //   amber:  UNCONFIRMED (email not verified yet)
            //   red:    FORCE_CHANGE_PASSWORD / RESET_REQUIRED / PASSWORD_RESET_REQUIRED
            //           (forgot-password silently no-ops for these —
            //           use magic-link to clear)
            //   gray:   ARCHIVED, UNKNOWN, anything else
            //   purple: COMPROMISED (rare but distinct severity)
            let bg = 'bg-gray-700 text-gray-200';
            const tip = ' title="' + s + '"';
            if (s === 'CONFIRMED') bg = 'bg-emerald-900 text-emerald-200';
            else if (s === 'UNCONFIRMED') bg = 'bg-amber-900 text-amber-200';
            else if (s === 'FORCE_CHANGE_PASSWORD' || s === 'RESET_REQUIRED' || s === 'PASSWORD_RESET_REQUIRED') bg = 'bg-red-900 text-red-200';
            else if (s === 'COMPROMISED') bg = 'bg-purple-900 text-purple-200';
            // Compact label — strip the verbose Cognito enum to fit.
            const short = s === 'FORCE_CHANGE_PASSWORD' ? 'force change pw'
              : s === 'PASSWORD_RESET_REQUIRED' || s === 'RESET_REQUIRED' ? 'reset required'
              : s.toLowerCase().replace(/_/g, ' ');
            return `<span class="px-1.5 py-0.5 rounded text-[10px] ${bg}"${tip}>${short}</span>`;
          },
        },
        {
          // Text filter (not the Enterprise default agSetColumnFilter)
          // so the floating filter input shows the actual typed/deep-
          // linked value. The set-filter dropdown UI couldn't surface
          // the value when /admin/users?email=<x> populated it via
          // setColumnFilterModel — the rows filtered but the column
          // header stayed blank (Mike caught it 2026-06-26).
          field: "attributes.email",
          headerName: 'Email',
          minWidth: 148,
          filter: 'agTextColumnFilter',
        },
        {
          field: "attributes.custom:role",
          headerName: 'Role',
          floatingFilterComponent: SetFloatingFilter,
          width: 100,
          suppressSizeToFit: true,
          editable: true,
          sort: 'asc',
          cellEditorSelector: (params: any) => {
            return {
              component: 'agSelectCellEditor',
              params: {
                values: this.roles
              }
            }
          },
          onCellValueChanged: this.on_user_edit.bind(this),
          cellStyle: (params: any) => {
            let style: any = {
              'text-align': 'center',
              'color': '#cdcdcd'
            };
            switch(params.value) {
              case 'admin':
                style.color = '#00b100';
                break;
              case 'client':
                style.color = '#6e9bda';
                break;
              default:
                break;
            }
            return style;
          },
        },
        {
          field: "attributes.custom:customers",
          headerName: 'Customers',
          minWidth: 108,
          filter: 'agTextColumnFilter',
          // Apply / Clear / Reset buttons in the column-menu popup so the
          // operator can pick "Equals" (or any non-default comparator)
          // and explicitly confirm. Without these, ag-grid's floating
          // filter input keeps using whatever comparator was last live
          // -- changing it in the popup but not applying never sticks,
          // which is the symptom Mike hit when looking for "show users
          // whose ONLY customer is X" (Equals on the comma-separated
          // value, not Contains).
          filterParams: {
            buttons: ['reset', 'apply'],
            closeOnApply: true,
            // Custom Blank / Not Blank options (numberOfInputs: 0 so the
            // floating filter input is hidden when selected) let an
            // operator filter rows that have NO custom:customers attr —
            // useful for cleanup audits ("who's missing a tenancy?").
            // Per Mike 2026-06-26.
            filterOptions: [
              'contains', 'equals', 'notEqual', 'startsWith', 'endsWith', 'notContains',
              {
                displayKey: 'blank',
                displayName: 'Blank',
                predicate: (_v: any, cellValue: any) => !cellValue || String(cellValue).trim() === '',
                numberOfInputs: 0,
              },
              {
                displayKey: 'notBlank',
                displayName: 'Not Blank',
                predicate: (_v: any, cellValue: any) => !!cellValue && String(cellValue).trim() !== '',
                numberOfInputs: 0,
              },
            ],
          },
          // floatingFilterComponent: SetFloatingFilter,
          cellRenderer: (params: any) => {
            let val = params.value || '';
            if (params.node.isSelected()) {
              return `<div class="flex items-center h-full">
                <button class="flex-0 mr-1 px-2 bg-app-teal-100 text-white text-xs rounded-xs cursor-pointer" data-button-action="edit-customers" title="Edit customers + scanners (unified access editor)">edit</button>
                <div class="flex-1 flex gap-1 text-xs">${val}</div>
              </div>`
            }
            else {
              let customers = val.split(',');
              let customer_names = ``;
              _.each(customers, (id: any) => {
                if (!id) return;
                customer_names += `<span class="bg-gray-400 text-xs text-black px-1 rounded-xs">${id}</span>`;
              });

              return `<div class="flex items-center h-full">
                <div class="flex-1 flex gap-1">${customer_names}</div>
              </div>`
            }
          }
        },
        {
          field: "attributes.custom:scanners",
          headerName: 'Scanners',
          minWidth: 108,
          floatingFilterComponent: SetFloatingFilter,
          cellRenderer: (params: any) => {
            let val = params.value || '';
            if (params.node.isSelected()) {
              return `<div class="flex items-center h-full">
                <button class="flex-0 mr-1 px-2 bg-app-teal-100 text-white text-xs rounded-xs cursor-pointer" data-button-action="edit-scanners">edit</button>
                <div class="flex-1 flex gap-1 text-xs">${val}</div>
              </div>`
            }
            else {
              let scanner_ids = val.split(',');
              let scanner_names = ``;
              _.each(scanner_ids, (id: any) => {
                if (!id) return;
                scanner_names += `<span class="bg-gray-400 text-xs text-black px-1 rounded-xs">${id}</span>`;
              });

              return `<div class="flex items-center h-full">
                <div class="flex-1 flex gap-1">${scanner_names}</div>
              </div>`
            }
          },
          editable: true,
          onCellValueChanged: this.on_user_edit.bind(this),
        },
        {
          field: "attributes.custom:notify_channels",
          headerName: 'Channels',
          minWidth: 148,
          floatingFilterComponent: SetFloatingFilter,
          // custom:notify_channels is a flat CSV of channel ids (e.g.
          // "as-1,as-7,as-12"). Inline edit was removed Mike 2026-06-28
          // when the scanner-grouped channels-editor modal landed —
          // editing CSV by hand made it easy to type unknown ids and
          // lose track of which scanner each id belonged to. Selected
          // row gets an "edit" button that opens the editor. Pills
          // stay for at-a-glance scannability.
          cellRenderer: (params: any) => {
            const val = String(params.value || '');
            const ids = val.split(/[,;\s]+/).map(t => t.trim()).filter(Boolean);
            const pills = ids.map(id =>
              `<span class="bg-gray-400 text-xs text-black px-1 rounded-xs">${this._html_escape(id)}</span>`
            ).join('');
            if (params.node.isSelected()) {
              return `<div class="flex items-center h-full">
                <button class="flex-0 mr-1 px-2 bg-app-teal-100 text-white text-xs rounded-xs cursor-pointer" data-button-action="edit-channels">edit</button>
                <div class="flex-1 flex gap-1">${pills}</div>
              </div>`;
            }
            return `<div class="flex items-center h-full">
              <div class="flex-1 flex gap-1">${pills}</div>
            </div>`;
          },
        },
        {
          field: "attributes.custom:claims",
          headerName: 'Claims',
          minWidth: 148,
          floatingFilterComponent: SetFloatingFilter,
          cellRenderer: (params: any) => {
            const raw = params.value || '';
            // Selected row gets an inline "edit" button that opens the
            // reusable ClaimsEditor modal (defined in shared/). Same
            // dispatch pattern the Customers column uses.
            const summary = this._summarize_claims(raw);
            if (params.node.isSelected()) {
              return `<div class="flex items-center h-full">
                <button class="flex-0 mr-1 px-2 bg-app-teal-100 text-white text-xs rounded-xs cursor-pointer" data-button-action="edit-claims">edit</button>
                <div class="flex-1 text-xs text-gray-300 truncate" title="${this._html_escape(raw)}">${this._html_escape(summary)}</div>
              </div>`;
            }
            return `<div class="flex items-center h-full">
              <div class="flex-1 text-xs text-gray-300 truncate" title="${this._html_escape(raw)}">${this._html_escape(summary)}</div>
            </div>`;
          },
        },
        {
          field: "attributes.custom:live_data_access",
          headerName: 'Live Data',
          width: 100,
          suppressSizeToFit: true,
          cellStyle: { 'text-align': 'center' },
          valueGetter: (params: any) => {
            let val = params.data.attributes['custom:live_data_access'];
            if (!val || val !== 'true') return false;
            else return true;
          },
          cellRenderer: (params: any) => {
            if (params.value === true) {
              return '<i class="fa fa-circle-check text-green-700"></i>';
            }
            else {
              return '';
            }
          },
          editable: true,
          cellEditorSelector: (params: any) => {
            return {
              component: 'agSelectCellEditor',
              params: {
                values: ['true', 'false']
              }
            }
          },
          onCellValueChanged: this.on_user_edit.bind(this),
        },
        {
          field: "attributes.custom:agreement_nasdaq",
          headerName: 'NASDAQ',
          width: 100,
          suppressSizeToFit: true,
          cellStyle: { 'text-align': 'center' },
          valueGetter: (params: any) => {
            let val = params.data.attributes['custom:agreement_nasdaq'];
            if (!val || val !== 'true') return false;
            else return true;
          },
          cellRenderer: (params: any) => {
            if (params.value === true) {
              return '<i class="fa fa-circle-check text-green-700"></i>';
            }
            else {
              return '';
            }
          },
          editable: true,
          cellEditorSelector: (params: any) => {
            return {
              component: 'agSelectCellEditor',
              params: {
                values: ['true', 'false']
              }
            }
          },
          onCellValueChanged: this.on_user_edit.bind(this),
        },
        {
          field: "attributes.custom:agreement_nyse",
          headerName: 'NYSE',
          width: 100,
          suppressSizeToFit: true,
          cellStyle: { 'text-align': 'center' },
          valueGetter: (params: any) => {
            let val = params.data.attributes['custom:agreement_nyse'];
            if (!val || val !== 'true') return false;
            else return true;
          },
          cellRenderer: (params: any) => {
            if (params.value === true) {
              return '<i class="fa fa-circle-check text-green-700"></i>';
            }
            else {
              return '';
            }
          },
          editable: true,
          cellEditorSelector: (params: any) => {
            return {
              component: 'agSelectCellEditor',
              params: {
                values: ['true', 'false']
              }
            }
          },
          onCellValueChanged: this.on_user_edit.bind(this),
        },
        {
          field: "attributes.custom:agreement_opra",
          headerName: 'OPRA',
          width: 100,
          suppressSizeToFit: true,
          cellStyle: { 'text-align': 'center' },
          valueGetter: (params: any) => {
            let val = params.data.attributes['custom:agreement_opra'];
            if (!val || val !== 'true') return false;
            else return true;
          },
          cellRenderer: (params: any) => {
            if (params.value === true) {
              return '<i class="fa fa-circle-check text-green-700"></i>';
            }
            else {
              return '';
            }
          },
          editable: true,
          cellEditorSelector: (params: any) => {
            return {
              component: 'agSelectCellEditor',
              params: {
                values: ['true', 'false']
              }
            }
          },
          onCellValueChanged: this.on_user_edit.bind(this),
        },
        {
          field: "attributes.custom:last_login",
          headerName: 'Last Login (ET)',
          filter: 'agDateColumnFilter',
          width: 178,
          suppressSizeToFit: true,
          valueGetter: (params: any) => params.data.attributes['custom:last_login'],
          valueFormatter: (params: any) => {
            if (!params.value) return '';
            const dt = DateTime.fromISO(params.value).setZone('America/New_York');
            return dt.toFormat('MM/dd/yyyy hh:mm:ss a');
          },
          comparator: (date1: string, date2: string) => {
            if (!date1 && !date2) return 0;
            if (!date1) return -1;
            if (!date2) return 1;
            const d1 = DateTime.fromISO(date1);
            const d2 = DateTime.fromISO(date2);
            return d1.toMillis() - d2.toMillis();
          },
          filterParams: {
            comparator: (filterLocalDateAtMidnight: Date, cellValue?: string) => {
              if (!cellValue) return -1;
              const cellDate = DateTime.fromISO(cellValue).startOf('day').toJSDate();
              return cellDate.getTime() - filterLocalDateAtMidnight.getTime();
            }
          }
        },
        {
          field: "attributes.custom:comments",
          headerName: 'Comments',
          minWidth: 148,
          floatingFilterComponent: SetFloatingFilter,
          editable: true,
          onCellValueChanged: this.on_user_edit.bind(this),
        },
        {
          field: "updated_at",
          headerName: 'Last Updated (ET)',
          filter: 'agDateColumnFilter',
          width: 178,
          suppressSizeToFit: true,
          valueGetter: (params: any) => params.data.updated_at,
          valueFormatter: (params: any) => {
            if (!params.value) return '';
            const dt = DateTime.fromISO(params.value).setZone('America/New_York');
            return dt.toFormat('MM/dd/yyyy hh:mm:ss a');
          },
          comparator: (date1: string, date2: string) => {
            if (!date1 && !date2) return 0;
            if (!date1) return -1;
            if (!date2) return 1;
            const d1 = DateTime.fromISO(date1);
            const d2 = DateTime.fromISO(date2);
            return d1.toMillis() - d2.toMillis();
          },
          filterParams: {
            comparator: (filterLocalDateAtMidnight: Date, cellValue?: string) => {
              if (!cellValue) return -1;
              const cellDate = DateTime.fromISO(cellValue).startOf('day').toJSDate();
              return cellDate.getTime() - filterLocalDateAtMidnight.getTime();
            }
          }
        },
        {
          field: "created_at",
          headerName: 'Created (ET)',
          filter: 'agDateColumnFilter',
          width: 178,
          suppressSizeToFit: true,
          valueGetter: (params: any) => params.data.created_at,
          valueFormatter: (params: any) => {
            if (!params.value) return '';
            const dt = DateTime.fromISO(params.value).setZone('America/New_York');
            return dt.toFormat('MM/dd/yyyy hh:mm:ss a');
          },
          comparator: (date1: string, date2: string) => {
            if (!date1 && !date2) return 0;
            if (!date1) return -1;
            if (!date2) return 1;
            const d1 = DateTime.fromISO(date1);
            const d2 = DateTime.fromISO(date2);
            return d1.toMillis() - d2.toMillis();
          },
          filterParams: {
            comparator: (filterLocalDateAtMidnight: Date, cellValue?: string) => {
              if (!cellValue) return -1;
              const cellDate = DateTime.fromISO(cellValue).startOf('day').toJSDate();
              return cellDate.getTime() - filterLocalDateAtMidnight.getTime();
            }
          }
        },
      ]
    }
  }
  /**
   * Clear the multi-email deep-link filter. Fires from the dismiss chip
   * above the grid. Preserves any other column filters the operator
   * added on top — this only revokes the external filter.
   */
  clearEmailListFilter(): void {
    this.emailListFilter = null;
    this.emailListRaw = [];
    try { this.grid?.api?.onFilterChanged?.(); } catch { /* ignore */ }
  }

  on_grid_ready(e: any) {
    this.grid.api = e.api;
    this.grid.colApi = e.colApi;
    this.grid.api.sizeColumnsToFit();
    // Kick the external filter check now that the api is available.
    // Without this, rows load before the grid knows to re-evaluate
    // isExternalFilterPresent, so the multi-email chip renders but the
    // grid shows every row until a subsequent event nudges it.
    if (this.emailListFilter && this.emailListFilter.size > 0) {
      try { this.grid.api.onFilterChanged(); } catch { /* ignore */ }
    }
    if (this.pendingClientFilter || this.pendingStatusFilter || this.pendingEmailFilter) {
      this.apply_deep_link_filters();
    }
    // Restore a full filter/sort snapshot from ?filters=<b64> if the
    // operator arrived via a shared link. Composes with the legacy
    // pending* params above — the base64 blob wins on any collision
    // because it carries the richer intent.
    this._apply_filters_query_param();
    // Open the live-users SSE now that the grid api is available.
    // Events arriving before grid-ready would have nowhere to land.
    this._open_user_event_source();
  }
  // Canonical hook for preloading filters that depend on rowData (set
  // filter values are derived from current rows). Fires once after the
  // first batch of rows actually renders.
  on_first_data_rendered(e: any) {
    if (this.pendingClientFilter || this.pendingStatusFilter || this.pendingEmailFilter) {
      this.apply_deep_link_filters();
    }
    this._apply_filters_query_param();
  }
  on_model_updated(e: any) {
    //Get the filtered rows from the grid
    if (this.grid?.api) {
      this.filtered_data = [];
      this.grid?.api.forEachNodeAfterFilter((node: any) => {
        this.filtered_data.push(node.data);
      });
    }
  }
  on_row_selected(e: any) {
    // Per-row selection callback -- fires N times on a header-checkbox
    // select-all (one per row). KEEP THIS LIGHT. The freeze Mike hit
    // came from refreshing every cell across three columns on every
    // single per-row fire: 844 rows x ~2500 cell refreshes each =
    // multiple million cell paints in one event-loop tick, which
    // locked the browser hard. Cell-refresh moved into
    // on_selection_changed (fires ONCE per user action) below.
    if (e?.node?.isSelected() === true) {
      this.selected_user = e.node.data;
    }
  }
  on_selection_changed(e: any) {
    const nodes = e?.api?.getSelectedNodes?.() ?? [];
    this.selected_users_for_password = nodes.map((n: any) => n.data).filter(Boolean);
    // One refresh per user action so the scanners / customers / claims
    // edit-button affordance updates on selection state changes. Was
    // being called per-row inside on_row_selected -- moved here so a
    // 844-row select-all fires ONE refreshCells, not 844.
    this.grid.api.refreshCells({
      columns: [
        'attributes.custom:scanners',
        'attributes.custom:customers',
        'attributes.custom:claims',
        'attributes.custom:notify_channels',
      ],
      force: true,
    });
  }

  // ngFor trackBy for the multi-select preview list. Without this, a
  // 1000-row selection re-builds every DOM child on every change-detection
  // tick, locking the browser. Keying by username keeps Angular's diff
  // O(N) instead of O(N^2).
  trackByUsername(_idx: number, u: any): string {
    return u?.username ?? u?.email ?? '';
  }

  // Generates a 16-char password that satisfies Cognito's default
  // strength policy (>= 1 upper, lower, digit, symbol). Each category
  // contributes a minimum, the rest is filled from the full pool and
  // shuffled, so the password is always policy-valid.
  generate_password(): string {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghijkmnopqrstuvwxyz';
    const digit = '23456789';
    const symbol = '!@#$%^&*()-_=+[]{}';
    const pool = upper + lower + digit + symbol;
    const pick = (chars: string) => chars[Math.floor(Math.random() * chars.length)];
    const out = [pick(upper), pick(lower), pick(digit), pick(symbol)];
    while (out.length < 16) out.push(pick(pool));
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out.join('');
  }

  // ----- Override Password mode (Customer stored vs Unique generated) -----
  // Operator-pickable source for the password. Mode preference persists
  // in localStorage so the same operator's typical choice (e.g. "I always
  // use the stored password") shows up next open. Default = 'stored' since
  // that's the support-friendly case (consistent password across users of
  // the same customer for the duration of an incident).
  private static readonly OVERRIDE_PW_MODE_LS_KEY = 'f2admin.override_pw_mode';
  // 'user_supplied' (third mode added 2026-06-24): user told us their
  // preferred password (e.g. over the phone); admin types it, applies as
  // permanent, no auto-generation, no stored-value side-effect. Different
  // from 'unique' which auto-generates + offers temporary flag.
  override_password_mode: 'stored' | 'unique' | 'user_supplied' | 'magic_link' = 'stored';
  // Held separately from override_password_value so the unique-mode
  // generated-random doesn't bleed into the user-supplied input when the
  // operator flips between modes.
  override_password_user_supplied = '';
  // distinct customer slugs in the selection — drives the stored-mode rows.
  override_password_distinct_customers: string[] = [];
  // slug → password (string) for the stored-mode editable per-customer rows.
  // Pre-filled from backend's stored value on modal open; empty if none.
  override_password_stored: { [slug: string]: string } = {};
  // slug → load-state (so each row can show a small "fetching..." hint).
  override_password_stored_loading: { [slug: string]: boolean } = {};

  /**
   * Open the override-password modal. `intent` picks which flow the
   * operator asked for from the Actions menu:
   *   'password'   → password-set flow. If saved mode was 'magic_link'
   *                  (from a prior Send Welcome Email open), fall back
   *                  to 'stored' so the modal doesn't ambush the
   *                  operator with the email flow.
   *   'magic_link' → force the welcome-email flow regardless of saved
   *                  mode. Persistence happens when the operator picks
   *                  a mode inside the modal (via setOverridePasswordMode).
   */
  open_override_password(intent: 'password' | 'magic_link' = 'password') {
    if (this.selected_users_for_password.length === 0) {
      this.toastService.warning('No users selected', 'Tick at least one row to override its password.');
      return;
    }
    // Restore mode preference from localStorage, then apply the intent
    // filter above.
    let mode: 'stored' | 'unique' | 'user_supplied' | 'magic_link' = 'stored';
    try {
      const saved = localStorage.getItem(UsersComponent.OVERRIDE_PW_MODE_LS_KEY);
      if (saved === 'stored' || saved === 'unique' || saved === 'user_supplied' || saved === 'magic_link') mode = saved;
    } catch { /* ignore */ }
    if (intent === 'magic_link') {
      mode = 'magic_link';
    } else if (mode === 'magic_link') {
      mode = 'stored';
    }
    this.override_password_mode = mode;
    this.override_password_value = this.generate_password();
    // Always clear the user-supplied field on open — never carry a value
    // from a previous session/modal-open (would silently apply the wrong
    // user's prior preference).
    this.override_password_user_supplied = '';
    this.override_password_temporary = true;
    this.override_password_busy = false;
    // Respect the grid's active Customers-column filter — when the
    // operator has narrowed the view to one customer, the modal should
    // only surface THAT customer's stored password (an admin/staff user
    // can legitimately have 8+ slugs in custom:customers; showing all
    // is noise when the operator's focused on a single customer).
    // Falls back to the distinct set across all selected users when no
    // filter is set.
    const filterModel = this.grid?.api?.getFilterModel?.() || {};
    const custFilter: any = filterModel['attributes.custom:customers'];
    const filterSlug = custFilter?.filter ? String(custFilter.filter).trim() : '';
    let distinct: string[];
    if (filterSlug) {
      distinct = [filterSlug];
    } else {
      const set = new Set<string>();
      for (const u of this.selected_users_for_password) {
        const raw = String(u?.attributes?.['custom:customers'] || '');
        for (const s of raw.split(',').map((x: string) => x.trim()).filter(Boolean)) {
          set.add(s);
        }
      }
      distinct = Array.from(set).sort();
    }
    // Normalize each slug to the canonical _id via directoryCustomers
    // aliases. Stored-password lookup is keyed by canonical _id on the
    // backend (per the alias-aware slug normalization landed earlier
    // today), so calling getStoredOverridePassword('hima') would miss
    // and surface "(none stored)" against the right customer. Dedupe
    // again post-normalize in case two legacy aliases collapsed onto
    // the same canonical row.
    const normalized = new Set<string>();
    for (const s of distinct) normalized.add(this._normalize_slug(s));
    this.override_password_distinct_customers = Array.from(normalized).sort();
    this.override_password_stored = {};
    this.override_password_stored_loading = {};
    // Fetch the stored password for each distinct customer in parallel.
    for (const slug of this.override_password_distinct_customers) {
      this.override_password_stored_loading[slug] = true;
      this.customerDirectoryService.getStoredOverridePassword(slug).then((v) => {
        this.override_password_stored[slug] = v?.password || '';
        this.override_password_stored_loading[slug] = false;
      });
    }
    this.showOverridePasswordModal = true;
  }

  close_override_password() {
    if (this.override_password_busy) return;
    this.showOverridePasswordModal = false;
    this.override_password_value = '';
  }

  /**
   * Actions menu → Send Welcome Email. Builds the user list from the
   * current bulk selection (email + pool_id + customer_slug + the
   * user's scanner_ids for that customer, parsed from custom:scanners)
   * and opens the dedicated Welcome Email preview+send modal. Does
   * NOT go through open_override_password('magic_link') — that path
   * is a different admin bulk-password-reset flow with different
   * template intent (Mike 2026-07-17).
   */
  openWelcomeEmailModal() {
    if (this.selected_users_for_password.length === 0) {
      this.toastService.warning('No users selected', 'Tick at least one row.');
      return;
    }
    // Grid rows can each carry multiple customer_slug entries. For the
    // welcome-email flow, emit ONE row per (user, customer_slug) pair
    // so the backend can route + label per-customer correctly. Scanner
    // ids come from the user's custom:scanners for that customer's
    // pool — we intersect against the F2-ADMIN.Scanners catalog for the
    // customer to expand `<customer>-*` wildcards + drop dead grants
    // isn't feasible on the SPA (no scanner-catalog membership here),
    // so we ship the raw grant list; the backend resolves labels.
    const inputs: WelcomeEmailUserInput[] = [];
    for (const u of this.selected_users_for_password) {
      const customerSlug = String(u?.customer_slug || '').trim();
      if (!customerSlug) continue;
      const scannerBlob = String(u?.attributes?.['custom:scanners'] || '').trim();
      const scannerIds = scannerBlob
        ? scannerBlob.split(',').map((s: string) => s.trim()).filter(Boolean)
        : [];
      inputs.push({
        email: String(u?.email || u?.attributes?.email || '').trim(),
        // Cognito username for the mint-magic-link path segment. Falls
        // back to email since most pools use username=email.
        username: String(u?.username || u?.email || u?.attributes?.email || '').trim() || undefined,
        pool_id: u?.pool_id ? String(u.pool_id).trim() : undefined,
        customer_slug: customerSlug,
        scanner_ids: scannerIds,
      });
    }
    this.welcomeEmailUsers = inputs.filter((x) => !!x.email);
    if (this.welcomeEmailUsers.length === 0) {
      this.toastService.warning('No routable rows',
        'Selected rows are missing email or customer_slug — cannot render a welcome email.');
      return;
    }
    this.showWelcomeEmailModal = true;
  }

  onWelcomeEmailClosed() { this.showWelcomeEmailModal = false; }
  onWelcomeEmailSent(_evt: { sent: number; failed: number; skipped: number }) {
    // No grid mutation needed — welcome emails don't change user state.
    this.showWelcomeEmailModal = false;
  }

  /**
   * From the Send Welcome Email modal's Skipped tab per-row "Copy magic
   * link" button. Look up the user by email in welcomeEmailUsers (which
   * carries username + pool_id since we populated them at open time),
   * mint the invite URL, copy to clipboard. Same primitives as the
   * Override Password → Copy magic-link URL path (adminService.mint-
   * MagicLink + copyText).
   */
  async onWelcomeEmailCopyMagicLink(evt: { email: string; customer_slug: string }) {
    const target = (this.welcomeEmailUsers || []).find(
      (u) => u.email === evt.email && (!evt.customer_slug || u.customer_slug === evt.customer_slug)
    );
    if (!target || !target.username) {
      this.toastService.error('Cannot mint magic link', 'Missing username on the selected row — reopen the modal and retry.');
      return;
    }
    const res = await this.adminService.mintMagicLink(target.username, target.pool_id);
    if (res?.err || !res?.url) {
      // mintMagicLink already toasted the error.
      return;
    }
    this.copyText(res.url, 'Magic-link URL');
  }

  /**
   * Is this user's pool magic-link-ready? Quick local check by
   * customer_slug: 'f2'/empty = legacy = not ready (legacy pool
   * isn't SES-wired by design). Per-customer pools assumed ready;
   * backend _mintInviteLinkForExistingUser still gates as the
   * authoritative refuse if any per-customer pool isn't fully wired.
   * Mike 2026-06-26 (ER-ST4HNV8).
   */
  isMagicLinkReady(user: any): boolean {
    const slug = String(user?.customer_slug || '').trim();
    return !!slug && slug !== 'f2';
  }

  /** Selected users whose pool isn't magic-link-ready. */
  get overridePasswordMagicLinkBlockedUsers(): any[] {
    return (this.selected_users_for_password || []).filter((u: any) => !this.isMagicLinkReady(u));
  }

  setOverridePasswordMode(mode: 'stored' | 'unique' | 'user_supplied' | 'magic_link') {
    this.override_password_mode = mode;
    // User-supplied default: permanent (it's the user's chosen password,
    // not a temp). Other modes preserve whatever the operator had set.
    if (mode === 'user_supplied') {
      this.override_password_temporary = false;
    }
    try { localStorage.setItem(UsersComponent.OVERRIDE_PW_MODE_LS_KEY, mode); } catch { /* ignore */ }
  }

  // Reseed one customer's stored password with a fresh random — operator
  // hits Generate to rotate without leaving the modal. Sticks only after
  // the operator clicks Apply (rotation lands as the side effect of
  // override_password's update_stored_for_customers).
  regenerateStoredPasswordFor(slug: string) {
    this.override_password_stored[slug] = this.generate_password();
  }

  regenerate_override_password() {
    this.override_password_value = this.generate_password();
  }

  copyText(text: string, label: string = 'Password') {
    try {
      navigator.clipboard?.writeText?.(text);
      this.toastService.success('Copied', `${label} copied to clipboard.`);
    } catch {
      /* clipboard unavailable -- nothing to do */
    }
  }

  copy_override_password() {
    this.copyText(this.override_password_value, 'Password');
  }

  // True if the current override-password selection includes ANY user
  // whose row came from the legacy pool. Drives the magic-link mode's
  // "legacy will fail" warning so it only shows when actually relevant
  // — pure-customer-pool selections don't need the warning.
  has_legacy_user_in_password_selection(): boolean {
    return (this.selected_users_for_password || []).some((u: any) => {
      const slug = String(u?.customer_slug || '').toLowerCase();
      return slug === 'f2' || slug === '' || slug === 'legacy';
    });
  }

  // Track in-flight mint so the button shows a loading state and prevents
  // double-click double-mint (would produce two valid tokens, only one
  // gets clipboard'd, the other is orphaned in Redis until TTL).
  override_password_copy_link_busy = false;

  /**
   * Magic-link mode: mint a magic-link URL for the single selected user
   * WITHOUT sending the email, copy to clipboard. Admin pastes it to
   * the user via Slack/SMS/etc. instead of relying on email delivery.
   */
  async copy_magic_link_for_selected() {
    const u = this.selected_users_for_password[0];
    if (!u?.username) {
      this.toastService.error('No user selected', 'Pick exactly one user before copying.');
      return;
    }
    this.override_password_copy_link_busy = true;
    try {
      const res = await this.adminService.mintMagicLink(u.username, u?.pool_id);
      if (res?.err || !res?.url) {
        // adminService.mintMagicLink already surfaced a toast — return.
        return;
      }
      this.copyText(res.url, 'Magic-link URL');
    } finally {
      this.override_password_copy_link_busy = false;
    }
  }

  cancel_override_password() {
    if (!this.override_password_busy) return;
    this.override_password_progress.cancelled = true;
  }

  // Per-user password resolver based on the active mode. Stored mode
  // picks the user's primary customer's stored password; if the user
  // has multiple customers, the FIRST one with a non-empty stored
  // value wins (operator can rotate the stored values from the modal
  // before applying).
  private _pwForUser(u: any): { pwd: string; updateForCustomers: string[] } {
    if (this.override_password_mode === 'user_supplied') {
      // User told the admin their preferred password out-of-band; admin
      // is setting it as the user's actual permanent password. We do NOT
      // touch the customer-stored override here — it's a one-time courtesy
      // application, not a stored-rotation event.
      return { pwd: this.override_password_user_supplied, updateForCustomers: [] };
    }
    if (this.override_password_mode === 'unique') {
      // One value applies to every user; we still record which customers
      // got it so the stored field updates as a side effect.
      const customers = String(u?.attributes?.['custom:customers'] || '')
        .split(',').map((s: string) => s.trim()).filter(Boolean);
      return { pwd: this.override_password_value, updateForCustomers: customers };
    }
    // stored mode — intersect the user's customers with the modal's
    // distinct list (which already respects any active grid filter).
    // First customer with a non-empty stored value wins.
    const customers = String(u?.attributes?.['custom:customers'] || '')
      .split(',').map((s: string) => s.trim()).filter(Boolean);
    const eligible = customers.filter((s) => this.override_password_distinct_customers.includes(s));
    for (const slug of eligible) {
      const v = (this.override_password_stored[slug] || '').trim();
      if (v) return { pwd: v, updateForCustomers: [slug] };
    }
    return { pwd: '', updateForCustomers: [] };
  }

  async submit_override_password() {
    const targets = [...this.selected_users_for_password];
    if (targets.length === 0) return;
    // Validate mode-specific inputs up front so we don't burn a partial
    // run with bad data.
    if (this.override_password_mode === 'unique') {
      const pwd = (this.override_password_value || '').trim();
      if (pwd.length < 8) {
        this.toastService.error('Password too short', 'Use at least 8 characters.');
        return;
      }
    } else if (this.override_password_mode === 'user_supplied') {
      const pwd = (this.override_password_user_supplied || '').trim();
      if (pwd.length < 8) {
        this.toastService.error('Password too short', 'Use at least 8 characters.');
        return;
      }
    } else if (this.override_password_mode === 'magic_link') {
      // Magic-link mode: nothing to validate up front. Backend will
      // refuse per-user if the pool isn't configured for branded SES
      // (legacy pool, etc.) — surfaces as per-user failure rather than
      // an up-front block, since some users in the selection might be
      // on per-customer pools and some on legacy.
    } else {
      // Stored mode: every selected user must resolve to a stored pwd
      // on at least one of their customers.
      const missing: string[] = [];
      for (const u of targets) {
        const { pwd } = this._pwForUser(u);
        if (!pwd || pwd.length < 8) missing.push(u?.attributes?.email || u?.username || '?');
      }
      if (missing.length) {
        this.toastService.error(
          'Stored password missing or too short',
          `${missing.length} user(s) have no stored password (or <8 chars) on any of their customers. First: ${missing[0]}`,
        );
        return;
      }
    }
    this.override_password_busy = true;
    this.override_password_progress = {
      total: targets.length,
      done: 0,
      ok: 0,
      fail: 0,
      cancelled: false,
      current_user: '',
    };
    // Capture per-user failures so the consolidated end-of-run report
    // carries actionable diagnostics in its body — without this, the
    // ErrorReport row shows only "No users were updated. See per-user
    // toasts for details." which is useless once the toasts have
    // dismissed. Each entry: { email, pool, reason }.
    const perUserFailures: { email: string; pool: string; reason: string }[] = [];
    for (const u of targets) {
      if (this.override_password_progress.cancelled) break;
      const username = u?.username;
      const email = u?.attributes?.email || username || '';
      const pool = u?.customer_slug || u?.pool_id || 'unknown';
      this.override_password_progress.current_user = email;
      // Force Angular's CD to tick (and the browser to paint the bar)
      // before the awaited API call -- without the yield, the progress
      // appears to stick because we're inside a microtask chain that
      // doesn't yield to the event loop.
      await new Promise(r => setTimeout(r, 0));
      if (!username) {
        this.override_password_progress.fail++;
        perUserFailures.push({ email: '(no username)', pool, reason: 'row missing username field' });
      } else if (this.override_password_mode === 'magic_link') {
        // Magic-link mode: backend mints a one-time invite token and
        // sends a branded "Welcome — set your password" email via the
        // user's pool's SES identity. No password to communicate; user
        // clicks email link → /accept-invite?token=... → sets their own
        // password → cleared FORCE_CHANGE_PASSWORD + ready to sign in.
        // Per-user pool_id routes to the right SES identity.
        const res = await this.adminService.sendMagicLink(username, u?.pool_id);
        if (res?.err) {
          this.override_password_progress.fail++;
          perUserFailures.push({ email, pool, reason: String(res.err).slice(0, 400) });
        }
        else this.override_password_progress.ok++;
      } else {
        const { pwd, updateForCustomers } = this._pwForUser(u);
        // pool_id is stamped on every row by get_users_across_pools so the
        // backend writes to the SAME pool the row came from. Same email can
        // exist in multiple pools post-Workstream-A; routing-by-row keeps
        // each override scoped to the customer the admin actually selected.
        const res = await this.adminService.overridePassword(
          username,
          pwd,
          this.override_password_temporary,
          updateForCustomers,
          u?.pool_id,
        );
        if (res?.err) {
          this.override_password_progress.fail++;
          perUserFailures.push({ email, pool, reason: String(res.err).slice(0, 400) });
        }
        else this.override_password_progress.ok++;
      }
      this.override_password_progress.done++;
    }
    this.override_password_busy = false;
    const { ok, fail, cancelled, total } = this.override_password_progress;
    // Build a compact JSON body for the consolidated ER so support can
    // see WHICH users failed against WHICH pool with WHICH AWS reason
    // — instead of the prior "see per-user toasts" dead end.
    const failureBody = perUserFailures.length
      ? JSON.stringify({ failures: perUserFailures.slice(0, 25), truncated: perUserFailures.length > 25 }).slice(0, 1800)
      : undefined;
    // Distinct reasons + pools roll up into the visible toast message so
    // the operator sees the dominant cause without having to click the
    // Support ID. Dedup keeps the line readable when 50 users hit the
    // same IAM denial.
    const distinctReasons = Array.from(new Set(perUserFailures.map(f => f.reason)));
    const distinctPools = Array.from(new Set(perUserFailures.map(f => f.pool)));
    if (cancelled) {
      this.toastService.warning(
        'Cancelled',
        `Stopped after ${ok + fail} of ${total}: ${ok} updated, ${fail} failed.`,
      );
    } else if (ok > 0 && fail === 0) {
      this.toastService.success(
        'Password override applied',
        `${ok} user${ok === 1 ? '' : 's'} updated${this.override_password_temporary ? ' (temporary -- user must change on next sign-in)' : ''}.`,
      );
      this.showOverridePasswordModal = false;
      this.override_password_value = '';
      this.override_password_user_supplied = '';
    } else if (ok > 0 && fail > 0) {
      // Partial-success keeps the warning toast (no ER row by default)
      // but we still mint a support-tracked report so the failures are
      // diagnosable later. Use successWithSupportId so the message is
      // copyable + the report carries the failure body.
      const firstReason = distinctReasons[0] || 'unknown';
      this.toastService.successWithSupportId(
        'Partial success',
        `${ok} updated, ${fail} failed in pool(s) ${distinctPools.join(', ')}. First reason: ${firstReason.slice(0, 160)}`,
        { error_code: 'F2_OVERRIDE_PASSWORD_PARTIAL', f2_response_body: failureBody },
      );
      this.showOverridePasswordModal = false;
      this.override_password_value = '';
      this.override_password_user_supplied = '';
    } else {
      // ALL failures — every per-user reason rolls into the ER body so
      // the report alone has enough to triage (was previously useless).
      const reasonLine = distinctReasons.length
        ? distinctReasons[0].slice(0, 200) + (distinctReasons.length > 1 ? ` (+${distinctReasons.length - 1} other reason${distinctReasons.length > 2 ? 's' : ''})` : '')
        : 'no per-user reason captured';
      this.toastService.error(
        'Override failed',
        `${fail} of ${total} failed in pool(s) ${distinctPools.join(', ') || '?'}. Reason: ${reasonLine}`,
        { error_code: 'F2_OVERRIDE_PASSWORD_ALL_FAIL', f2_response_body: failureBody },
      );
    }
  }

  // Returns the target's `custom:scanners` parsed -- supports both
  // literal IDs ('alpha-eye') and wildcards ('tt-*'). Wildcards get
  // expanded against the loaded scanner catalog so the picker shows
  // real options rather than the abstract pattern.
  impersonate_target_scanner_ids(): string[] {
    const raw = (this.impersonate_target?.attributes?.['custom:scanners'] || '') as string;
    const entries = raw.split(',').map((s) => s.trim()).filter(Boolean);
    const concrete: string[] = [];
    for (const e of entries) {
      if (!e.endsWith('*')) { concrete.push(e); continue; }
      const prefix = e.slice(0, -1);
      for (const s of this.scanners || []) {
        const id = String((s as any)?._id || '');
        if (id.startsWith(prefix) && !concrete.includes(id)) concrete.push(id);
      }
    }
    return concrete;
  }

  open_impersonate(user: any) {
    if (!user) return;
    this.impersonate_target = user;
    this.impersonate_kind = 'members';
    this.impersonate_scanner_id = '';
    this.impersonate_busy = false;
    this.impersonate_result_url = '';
    this.impersonate_result_target_email = '';
    this.impersonate_result_target_display = '';
    this.impersonate_result_expires_at = '';
    this.showImpersonateModal = true;
  }

  // Build a "First Last" name from the target's Cognito attributes.
  // Returns empty string when no name attributes are set — the
  // template uses that as the trigger to fall back to email-only.
  //
  // F2's shared Cognito pool convention stores FIRST NAME in the
  // standard `name` attribute (NOT `given_name`) and last name in
  // `family_name` — see f2-admin-service helper_get_user_details
  // which reads `first_name: p_user.attributes.name`. The earlier
  // `given_name` lookup here returned empty for every legacy-pool
  // user, so only the last name appeared in the impersonate modal.
  impersonate_target_name(): string {
    const attrs = this.impersonate_target?.attributes || {};
    const f = (attrs['name'] || attrs['given_name'] || '').trim();
    const l = (attrs['family_name'] || '').trim();
    return [f, l].filter(Boolean).join(' ');
  }

  // IT-F2-421 (Mike 2026-09-23 chat): shared row-level pool label that
  // splits f2-dev-pool (legacy env pool) from f2-users-pool (F2's own
  // pool) via pool_id. Called from the delete-confirm modal + anywhere
  // else the raw customer_slug alone is ambiguous.
  pool_label_for_row(row: any): string {
    const slug = String(row?.customer_slug || '').trim();
    const rowPoolId = String(row?.pool_id || '').trim();
    if (!slug) return 'dev (legacy)';
    if (slug === 'f2') {
      const f2Cust = (this.directoryCustomers || []).find((c: any) => String(c?._id || '') === 'f2');
      const f2OwnPoolId = String(f2Cust?.cognito_pool?.user_pool_id || '').trim();
      if (rowPoolId && f2OwnPoolId && rowPoolId === f2OwnPoolId) return 'f2';
      return 'dev (legacy)';
    }
    return slug;
  }

  // Friendly display of the target's Cognito pool. Mirrors the Pool
  // column chip in the Users grid: split f2-dev-pool (legacy env pool)
  // from f2-users-pool (F2 customer's own pool). IT-F2-421 chat 2026-09-23.
  impersonate_target_pool_label(): string {
    const slug = String(this.impersonate_target?.customer_slug || '').trim();
    const rowPoolId = String(this.impersonate_target?.pool_id || '').trim();
    if (!slug) return 'dev (legacy)';
    if (slug === 'f2') {
      const f2Cust = (this.directoryCustomers || []).find((c: any) => String(c?._id || '') === 'f2');
      const f2OwnPoolId = String(f2Cust?.cognito_pool?.user_pool_id || '').trim();
      if (rowPoolId && f2OwnPoolId && rowPoolId === f2OwnPoolId) return 'f2';
      return 'dev (legacy)';
    }
    return slug;
  }

  // Email to display in the Target panel. attributes.email is the
  // canonical source; we fall back to top-level email (legacy row shape)
  // and finally to username (UUID for per-customer-pool users — never
  // ideal but at least non-empty when the row really has no email).
  impersonate_target_email_display(): string {
    return this.impersonate_target?.attributes?.email
      || this.impersonate_target?.email
      || this.impersonate_target?.username
      || '';
  }

  close_impersonate() {
    if (this.impersonate_busy) return;
    this.showImpersonateModal = false;
    this.impersonate_target = null;
    this.impersonate_result_url = '';
    this.impersonate_result_target_email = '';
    this.impersonate_result_target_display = '';
    this.impersonate_result_expires_at = '';
  }

  // Human label for a scanner slug, used in the picker so admins see
  // e.g. "Alpha Shark — Option Sniper (as-os)" instead of the raw id.
  // Falls back to the slug alone when the catalog hasn't loaded yet OR
  // a wildcard expansion produced a slug that's no longer in F2-ADMIN.
  scanner_label(slug: string): string {
    if (!slug) return '';
    const found = (this.scanners || []).find((s: any) => String(s?._id) === slug);
    const name = found?.Name?.trim();
    return name ? `${name} (${slug})` : slug;
  }

  copy_impersonate_url() {
    if (!this.impersonate_result_url) return;
    try {
      navigator.clipboard?.writeText?.(this.impersonate_result_url);
      this.toastService.success('Copied', 'Impersonation URL copied to clipboard.');
    } catch {
      /* clipboard unavailable -- silent */
    }
  }

  // Formats an ISO timestamp like "Jun 16 3:42 PM ET" for the result
  // panel's expiry line. Falls back to the raw value on parse error so
  // the operator still sees something.
  fmtDate(iso: string): string {
    if (!iso) return '';
    try {
      return DateTime.fromISO(iso).setZone('America/New_York').toFormat('LLL d h:mm a') + ' ET';
    } catch {
      return iso;
    }
  }

  reopen_impersonate_url() {
    if (!this.impersonate_result_url) return;
    window.open(this.impersonate_result_url, '_blank', 'noopener,noreferrer');
  }

  async submit_impersonate() {
    if (!this.impersonate_target?.username) return;
    if (this.impersonate_kind === 'scanner' && !this.impersonate_scanner_id) {
      this.toastService.warning('Pick a scanner', 'Choose which scanner to view as the target.');
      return;
    }
    this.impersonate_busy = true;
    // Pass pool_id so the backend looks up the target user in the right
    // Cognito pool. Without it, get_user_details defaults to legacy and
    // a per-customer-pool user's email lookup falls through to the sub
    // UUID — surfaces in audit ERs as e.g. target_email="c478e438-...".
    // Per row-level routing migration (runbook:per-customer-pool-routing
    // -all-admin-writes-2026-06-25).
    const res = await this.adminService.impersonate(
      this.impersonate_target.username,
      this.impersonate_kind,
      this.impersonate_kind === 'scanner' ? this.impersonate_scanner_id : undefined,
      this.impersonate_target?.pool_id || undefined,
    );
    this.impersonate_busy = false;
    if (res?.err || !res?.url) return;
    // Open immediately AND keep the URL on the modal so admin can copy
    // it (with the target email already baked in) to share with a
    // support teammate when troubleshooting.
    window.open(res.url, '_blank', 'noopener,noreferrer');
    this.impersonate_result_url = res.url;
    this.impersonate_result_target_email = res.target_email || this.impersonate_target_email_display();
    // Backend a700286 returns target_display = "First Last <email>".
    // Fall back to local synthesis when the backend hasn't been deployed
    // yet OR the user had no name attributes set.
    const localName = this.impersonate_target_name();
    this.impersonate_result_target_display = res.target_display
      || (localName ? `${localName} <${this.impersonate_result_target_email}>` : this.impersonate_result_target_email);
    this.impersonate_result_expires_at = res.expires_at || '';
    const mins = res.ttl_seconds ? Math.round(res.ttl_seconds / 60) : null;
    this.toastService.success(
      'Impersonation tab opened',
      `Viewing as ${this.impersonate_result_target_display}` +
        (mins ? ` -- URL stays valid for ~${mins} min.` : '.'),
    );
  }

  on_row_clicked(e: any) {
    if (e.event.target !== undefined) {
      let user = e.data;
      let action = e.event.target.getAttribute('data-button-action');
      switch (action) {
        // Mike 2026-06-28: customers + scanners edit both route to the
        // unified access editor — same collapsible per-customer cards
        // as the claims / channels editors. Old per-attribute modals
        // (showEditUserScannersModal / showEditUserCustomersModal)
        // kept in source as dead code until I'm sure nothing else
        // calls them, then will be deleted.
        case 'edit-scanners':
        case 'edit-customers':
        case 'edit-access':
          this.on_edit_user_access(user);
          break;
        case 'edit-claims':
          this.on_edit_user_claims(user);
          break;
        case 'edit-channels':
          this.on_edit_user_channels(user);
          break;
        default:
          break;
      }
    }
  }
  async get_scanner_values() {
    if (!this.scanners || this.scanners.length === 0) {
      await this.get_scanners();
    }

    if (!this.scanners.length) return [];

    return this.scanners.map((s: any) => {
      return s.Id;
    });
  }

  /**** Actions ****/
  //Create User
  on_add_user_click() {
    this.newUser.name = '';
    this.newUser.last_name = '';
    this.newUser.email = '';
    this.newUser.password = '';
    this.newUser.role = '';
    this.newUser.customers = '';
    this.newUser.scanners = '';
    // Reset email-pre-flight state so the picker isn't carrying stale
    // disabled-flags from a previous open.
    this.add_email_existing_pool_ids = [];
    this.add_email_lookup_busy = false;
    // Default is now DERIVED from the selected customer's
    // commerce_settings.welcome_email_enabled — reset the derivation
    // state so the pool-change / customer-change handlers can lay in
    // a fresh default once a pool/customer is picked. Provisional true
    // until a pool is chosen (matches the pre-2026-07-17 behavior for
    // the brief window before pool selection is made).
    this.newUser.send_magic_link = true;
    this.add_user_customer_default_welcome = null;
    this.add_user_customer_default_source_slug = '';
    this.selected_add_customers = [];
    this.selected_add_scanners = [];
    this.add_selected_pool_id = null;
    // Reset per-scanner claims + channels so a previous Add User run's
    // JSON doesn't leak into the next user.
    this._addUserPerScannerClaims.clear();
    this._addUserPerScannerChannels.clear();
    this._addUserExpandedChannelsFor.clear();
    this.showAddUserModal = true;
  }

  // IT-F2-106 Phase 3a: per-scanner inline channels picker. Helpers
  // consumed by the accordion rendered under each scanner-enabled row.

  /** Scanner doc for a given id, or undefined if the catalog hasn't loaded
   *  it (defensive — shouldn't happen for Add-User picks). */
  private _getScannerDoc(scannerId: string): any {
    return (this.scanners || []).find((s: any) => String(s?._id || s?.Id || '') === scannerId);
  }
  /** Public alias for the template — Angular strict templates disallow
   *  reading `_`-prefixed members. Called from the inline claims/channels
   *  bindings in the scanner accordion. */
  _getScannerDocForTemplate(scannerId: string): any {
    return this._getScannerDoc(scannerId);
  }

  /** Ordered channels[] declared on a scanner. Empty when scanner has no
   *  channels — used by the template to hide the sub-panel. */
  addUserScannerChannels(scannerId: string): any[] {
    const s = this._getScannerDoc(scannerId);
    return Array.isArray(s?.channels) ? s.channels : [];
  }

  /** Whether the Channels sub-panel should render open under this scanner.
   *  Auto-opens on enable (see on_add_scanner_change); operator can toggle
   *  independently via chevron without unchecking the scanner. */
  isChannelsPanelExpanded(scannerId: string): boolean {
    return this._addUserExpandedChannelsFor.has(scannerId);
  }

  toggleChannelsPanel(scannerId: string) {
    if (this._addUserExpandedChannelsFor.has(scannerId)) {
      this._addUserExpandedChannelsFor.delete(scannerId);
    } else {
      this._addUserExpandedChannelsFor.add(scannerId);
    }
  }

  isChannelPicked(scannerId: string, channelId: string): boolean {
    return this._addUserPerScannerChannels.get(scannerId)?.has(channelId) === true;
  }

  toggleChannelPick(scannerId: string, channelId: string) {
    let set = this._addUserPerScannerChannels.get(scannerId);
    if (!set) {
      set = new Set<string>();
      this._addUserPerScannerChannels.set(scannerId, set);
    }
    if (set.has(channelId)) set.delete(channelId);
    else set.add(channelId);
  }

  selectAllChannelsForScanner(scannerId: string) {
    const chans = this.addUserScannerChannels(scannerId);
    const set = new Set<string>();
    for (const ch of chans) if (ch?.id) set.add(String(ch.id));
    this._addUserPerScannerChannels.set(scannerId, set);
  }

  clearChannelsForScanner(scannerId: string) {
    this._addUserPerScannerChannels.set(scannerId, new Set<string>());
  }

  channelsPickedCountForScanner(scannerId: string): number {
    return this._addUserPerScannerChannels.get(scannerId)?.size || 0;
  }

  // IT-F2-106 Phase 3c: whether a scanner has a claims_schema (own or
  // fallback via Customer.claims_schema with template.scanner pinning).
  // Used by the template to hide the per-scanner Edit-claims button
  // when the scanner has nothing to configure.
  addUserScannerHasClaims(scannerId: string): boolean {
    const s = this._getScannerDoc(scannerId);
    if (s?.claims_schema) return true;
    const custSlug = String(s?.Client || '');
    const cust = custSlug ? (this.directoryCustomers || []).find((c: any) => String(c?._id || '') === custSlug) : undefined;
    const custSchema = (cust as any)?.claims_schema;
    if (!custSchema) return false;
    const pin = custSchema?.template?.scanner || '';
    return !pin || pin === scannerId;
  }

  /**
   * True when at least one of the currently-picked scanners has a
   * `claims_schema` — either its own or the fallback via
   * Customer.claims_schema (unpinned or pinned to this scanner). Used
   * to hide the Claims row entirely when none of the picks have any
   * settable claims. Mike 2026-07-02: alpha-shark Add User showed the
   * Claims / Channels rows for as-os but the editor opened empty for
   * scanners with no channels; the row should just be hidden.
   */
  get add_user_any_scanner_has_claims_schema(): boolean {
    const picks = this._add_user_expanded_scanner_ids();
    if (picks.length === 0) return false;
    const scannerById = new Map<string, any>();
    for (const s of (this.scanners || [])) {
      const id = String(s?.Id || s?._id || '');
      if (id) scannerById.set(id, s);
    }
    const customerById = new Map<string, any>();
    for (const c of (this.directoryCustomers || [])) customerById.set(String(c?._id || ''), c);
    for (const scannerId of picks) {
      const s = scannerById.get(scannerId);
      if (s?.claims_schema) return true;
      const custSlug = String(s?.Client || '');
      const cust = custSlug ? customerById.get(custSlug) : undefined;
      const custSchema = cust?.claims_schema;
      if (!custSchema) continue;
      // Same fallback rule as the editor: the customer's schema
      // applies to this scanner UNLESS template.scanner pins a
      // different scanner id.
      const pin = custSchema?.template?.scanner || '';
      if (!pin || pin === scannerId) return true;
    }
    return false;
  }

  /**
   * True when at least one picked scanner has a non-empty `channels`
   * array (Scanners.<id>.channels). Hides the Channels row when none
   * of the picks emit channels.
   */
  get add_user_any_scanner_has_channels(): boolean {
    const picks = this._add_user_expanded_scanner_ids();
    if (picks.length === 0) return false;
    const scannerById = new Map<string, any>();
    for (const s of (this.scanners || [])) {
      const id = String(s?.Id || s?._id || '');
      if (id) scannerById.set(id, s);
    }
    for (const scannerId of picks) {
      const ch = scannerById.get(scannerId)?.channels;
      if (Array.isArray(ch) && ch.length > 0) return true;
    }
    return false;
  }

  /**
   * Expand selected_add_scanners into concrete scanner ids (wildcards
   * matched against the loaded Scanners list). Shared internal for
   * add_user_scanners_for_editor + the two "any scanner has …" gates.
   */
  private _add_user_expanded_scanner_ids(): string[] {
    const wildcards = new Set<string>();
    const explicit = new Set<string>();
    for (const sid of this.selected_add_scanners || []) {
      if (sid.endsWith('*')) wildcards.add(sid);
      else explicit.add(sid);
    }
    if (wildcards.size) {
      for (const s of this.scanners || []) {
        const id = String(s?.Id || s?._id || '');
        if (!id) continue;
        for (const w of wildcards) {
          if (id.startsWith(w.slice(0, -1))) explicit.add(id);
        }
      }
    }
    return Array.from(explicit);
  }

  /**
   * Comma-joined scanner IDs (no wildcards) from the Add User modal's
   * selected_add_scanners. Wildcards like `tt-*` are expanded to the
   * concrete scanner IDs the wildcard covers so the claims / channels
   * editors can render per-scanner tabs. Used only by the editor
   * modals — the create-user submit path keeps wildcards intact for
   * custom:scanners.
   */
  get add_user_scanners_for_editor(): string {
    return this._add_user_expanded_scanner_ids().sort().join(',');
  }

  /**
   * Groups every directoryCustomer by cognito_pool.user_pool_id so the
   * Add User modal can render one radio per pool. The empty-string
   * pool_id is the legacy shared pool (Customers with no
   * cognito_pool.user_pool_id — Mike's rule 2026-06-26: "missing that
   * is a legacy customer"). Order: legacy first (if any customers land
   * there), then dedicated pools in slug order.
   *
   * `is_legacy=true` groups render their slug list as checkboxes
   * (multiple slugs share the pool, user can attach to N at once).
   * `is_legacy=false` groups render a single auto-selected slug (the
   * dedicated pool is 1:1 with its customer). `taken=true` means
   * newUser.email already has an identity in this pool — the radio
   * disables so we don't try to create a duplicate.
   */
  get add_pool_groups(): Array<{
    pool_id: string;
    label: string;
    is_legacy: boolean;
    slugs: string[];
    taken: boolean;
  }> {
    // Scope filter — a client / client_admin can only create users
    // inside the customers listed on their own custom:customers. Backend
    // enforces this too (POST /rest/admin/users returns "At least one
    // customer in your scope is required." — ER-ASWJRE9 2026-07-02), but
    // hiding the out-of-scope options at picker time is friendlier than
    // an error toast after submit. Super-admin sees everything.
    const isAdmin = this.authService.is_admin();
    const callerScope = new Set(
      (this.authService.get_customers() || []).map((s: string) => String(s || '').trim()).filter(Boolean),
    );
    const inScope = (slug: string) => isAdmin || callerScope.has(slug);

    const byPool = new Map<string, string[]>();
    const legacySlugs: string[] = [];
    for (const c of (this.directoryCustomers || [])) {
      const slug = String(c?._id || '').trim();
      if (!slug) continue;
      if (!inScope(slug)) continue;
      const pid = String(c?.cognito_pool?.user_pool_id || '').trim();
      if (!pid) {
        legacySlugs.push(slug);
      } else {
        const arr = byPool.get(pid) || [];
        arr.push(slug);
        byPool.set(pid, arr);
      }
    }
    const groups: Array<{ pool_id: string; label: string; is_legacy: boolean; slugs: string[]; taken: boolean }> = [];
    if (legacySlugs.length) {
      groups.push({
        pool_id: '',
        label: 'Legacy pool (shared)',
        is_legacy: true,
        slugs: legacySlugs.sort(),
        taken: this._is_add_pool_taken(''),
      });
    }
    const dedicated = Array.from(byPool.entries())
      .map(([pid, slugs]) => ({
        pool_id: pid,
        label: `${slugs.sort().join(', ')} (dedicated pool)`,
        is_legacy: false,
        slugs,
        taken: this._is_add_pool_taken(pid),
      }))
      .sort((a, b) => a.slugs[0].localeCompare(b.slugs[0]));
    return [...groups, ...dedicated];
  }

  /** trackBy for the *ngFor over add_pool_groups. The getter returns a
   *  fresh array + fresh group objects on every CD tick, so without a
   *  stable identity Angular tears down and rebuilds every radio DOM
   *  node on any event (tab, space, click) — killing focus on whichever
   *  radio the operator was on. Keyed by pool_id (legacy uses '', still
   *  unique). Mike 2026-07-27. */
  trackByPoolId(_i: number, g: { pool_id: string }) { return g.pool_id; }
  /** Same fix for the nested legacy-slug checkboxes. Slugs are unique
   *  within the legacy pool group. */
  trackByString(_i: number, s: string) { return s; }
  /** Same fix for the customer scanner groups + their inner scanner
   *  rows. Getter returns fresh arrays + fresh group objects each CD
   *  tick, so without trackBy Angular tears down/rebuilds every scanner
   *  accordion row on every event — which destroyed our inline claims
   *  component on every mousedown (likely the actual root cause of the
   *  Phase 3b freeze + the dropdown-close-on-hover). */
  trackByCustomer(_i: number, g: { customer: string }) { return g.customer; }
  trackByScannerId(_i: number, s: { Id?: string; _id?: string }) { return s?.Id || s?._id; }

  /** Whether newUser.email already exists in the pool identified by pool_id.
   *  Empty pool_id (legacy) treats the presence of any existing identity
   *  NOT in a known per-customer pool as "legacy is taken". */
  private _is_add_pool_taken(pool_id: string): boolean {
    if (!this.add_email_existing_pool_ids.length) return false;
    if (!pool_id) {
      const knownPerCustomerPools = new Set<string>();
      for (const c of (this.directoryCustomers || [])) {
        const p = String(c?.cognito_pool?.user_pool_id || '').trim();
        if (p) knownPerCustomerPools.add(p);
      }
      return this.add_email_existing_pool_ids.some(p => !knownPerCustomerPools.has(p));
    }
    return this.add_email_existing_pool_ids.includes(pool_id);
  }

  /**
   * Radio-change handler: reset the customer + scanner selections
   * whenever the operator flips pools. For a dedicated (non-legacy)
   * pool the single owned slug is auto-selected so the operator
   * doesn't need a redundant checkbox tick; for legacy the operator
   * picks which subset of legacy slugs to attach.
   */
  on_add_pool_change(g: { pool_id: string; is_legacy: boolean; slugs: string[] }) {
    this.add_selected_pool_id = g.pool_id;
    this.selected_add_customers = g.is_legacy ? [] : [...g.slugs];
    this.selected_add_scanners = [];
    // Legacy pool has no branded SES identity — magic-link mint would
    // fall back to Cognito default sender and get flagged as phishing
    // (backend refuses, ER-6JQDRFJ 2026-07-01). Force welcome-email OFF
    // for legacy regardless of any customer default. Dedicated pools
    // fetch the customer's commerce_settings.welcome_email_enabled and
    // use THAT as the default — so an SSO/OEM tenant with the setting
    // OFF gets an unchecked box and doesn't send a surprise email.
    if (g.is_legacy) {
      this.newUser.send_magic_link = false;
      this.add_user_customer_default_welcome = null;
      this.add_user_customer_default_source_slug = '';
    } else {
      const slug = g.slugs?.[0] || '';
      if (slug) this._apply_customer_welcome_default(slug);
      else this.newUser.send_magic_link = true;
    }
  }

  /**
   * Fetch F2-ADMIN.Customers.<slug>.commerce_settings.welcome_email_enabled
   * and use it as the Add-User modal's send_magic_link default. Stashes
   * the resolved value + source slug in state so the modal can render an
   * explicit "Customer default: ON/OFF" hint under the checkbox.
   *
   * Called by:
   *   - on_add_pool_change (dedicated pool picked → single slug lookup)
   *   - on_add_customer_change (legacy pool → first checked slug wins)
   *   - Add-User modal open reset is a no-op — we wait until a pool /
   *     customer is picked so we know WHICH customer to look up.
   */
  private async _apply_customer_welcome_default(slug: string) {
    if (!slug) return;
    this.add_user_customer_default_source_slug = slug;
    try {
      const res: any = await this.customerDirectoryService.getCommerceSettings(slug);
      const enabled = !!res?.welcome_email_enabled;
      this.add_user_customer_default_welcome = enabled;
      this.newUser.send_magic_link = enabled;
    } catch {
      // Lookup failure: leave the default null (hint reads "unknown"
      // in the template) but don't clobber whatever the operator may
      // have already set on the checkbox.
      this.add_user_customer_default_welcome = null;
    }
  }
  // Distinct Client values sourced from the loaded scanners list.
  get available_add_customers(): string[] {
    const set = new Set<string>();
    for (const s of this.scanners) if (s?.Client) set.add(s.Client);
    return Array.from(set).sort();
  }
  // Build groups for the currently-selected customers. Each group has the
  // customer label, the prefix (longest common scanner-ID prefix), the
  // resulting wildcard token (e.g. 'tt-*'), and the customer's scanners.
  get add_customer_scanner_groups(): { customer: string; prefix: string; wildcard: string; scanners: any[] }[] {
    const groups: any[] = [];
    for (const c of this.selected_add_customers) {
      const scs = this.scanners.filter((s: any) => s.Client === c);
      if (!scs.length) continue;
      const prefix = this._longest_common_prefix(scs.map((s: any) => s.Id));
      // If no common prefix, fall back to the customer name plus dash so the
      // wildcard token is still unique per customer.
      const wildcard = prefix ? `${prefix}*` : `${c}-*`;
      groups.push({ customer: c, prefix, wildcard, scanners: scs });
    }
    return groups;
  }
  _longest_common_prefix(strs: string[]): string {
    if (!strs.length) return '';
    let pref = strs[0];
    for (let i = 1; i < strs.length; i++) {
      while (strs[i].indexOf(pref) !== 0) {
        pref = pref.slice(0, -1);
        if (!pref) return '';
      }
    }
    return pref;
  }
  on_add_customer_change(customer: string, event: any) {
    const checked = event.target.checked;
    if (checked) {
      if (!this.selected_add_customers.includes(customer)) {
        this.selected_add_customers.push(customer);
      }
    } else {
      _.remove(this.selected_add_customers, (c: any) => c === customer);
      // Drop any scanner selections (exact or wildcard) tied to this customer.
      const scs = this.scanners.filter((s: any) => s.Client === customer);
      const ids = scs.map((s: any) => s.Id);
      const prefix = this._longest_common_prefix(ids);
      const wildcard = prefix ? `${prefix}*` : `${customer}-*`;
      this.selected_add_scanners = this.selected_add_scanners.filter((sid: string) => {
        if (sid === wildcard) return false;
        if (ids.includes(sid)) return false;
        return true;
      });
    }
    // Re-apply the customer's welcome-email default whenever the
    // selection set changes. First selected customer wins — same
    // resolution rule the dedicated-pool path uses. Skipped when the
    // selection is empty (nothing to look up) or when we're on a
    // legacy pool with no branded SES anyway.
    const firstSlug = this.selected_add_customers[0] || '';
    if (firstSlug && this.add_selected_pool_id !== '') {
      this._apply_customer_welcome_default(firstSlug);
    } else if (!firstSlug) {
      this.add_user_customer_default_welcome = null;
      this.add_user_customer_default_source_slug = '';
    }
  }

  // ----- Add-User multi-pool routing helpers -----

  /**
   * Cognito pool_id for a customer slug, read from F2-ADMIN.Customers
   * source-of-truth (directoryCustomers[slug].cognito_pool.user_pool_id).
   * Returns null when the customer has no per-customer pool — that's
   * "legacy customer" by definition: their user lives in the shared
   * legacy pool. Mike 2026-06-26: "should be determined dynamic by
   * the customer source of truth's cognito pools setting; missing that
   * is a legacy customer."
   */
  pool_id_for_customer_slug(slug: string): string | null {
    if (!slug) return null;
    const c = (this.directoryCustomers || []).find((x: any) => String(x?._id) === slug);
    const pid = String(c?.cognito_pool?.user_pool_id || '').trim();
    return pid || null;
  }

  /** True iff this customer's pool already has an identity with newUser.email. */
  is_add_customer_pool_taken(slug: string): boolean {
    if (!this.add_email_existing_pool_ids.length) return false;
    const pid = this.pool_id_for_customer_slug(slug);
    // Legacy customers (null pid) map to the legacy pool. The backend
    // index records legacy entries under the legacy pool_id (this.userPool
    // on the backend); the SPA doesn't know that literal, so we accept
    // any of the user's existing identities as "legacy-taken" when the
    // customer is legacy AND the SET contains at least one entry that
    // does NOT match any known per-customer pool. Conservative: when
    // legacy and the lookup says "exists somewhere", treat as taken.
    if (!pid) {
      const knownPerCustomerPools = new Set<string>();
      for (const c of (this.directoryCustomers || [])) {
        const p = String(c?.cognito_pool?.user_pool_id || '').trim();
        if (p) knownPerCustomerPools.add(p);
      }
      return this.add_email_existing_pool_ids.some(p => !knownPerCustomerPools.has(p));
    }
    return this.add_email_existing_pool_ids.includes(pid);
  }

  /** Group selected customer slugs by their resolved pool_id (legacy = ''). */
  group_selected_customers_by_pool(): Array<{ pool_id: string | null; slugs: string[] }> {
    const map = new Map<string, string[]>();
    for (const slug of this.selected_add_customers) {
      const pid = this.pool_id_for_customer_slug(slug) || '';
      const arr = map.get(pid) || [];
      arr.push(slug);
      map.set(pid, arr);
    }
    return Array.from(map.entries()).map(([pid, slugs]) => ({
      pool_id: pid || null,
      slugs,
    }));
  }

  /** Slugs whose pool resolved to a duplicate — used for explanatory toast. */
  selected_taken_slugs(): string[] {
    return (this.selected_add_customers || []).filter(s => this.is_add_customer_pool_taken(s));
  }

  /**
   * Debounced lookup of which pools already contain newUser.email. Fires
   * 350ms after the last keystroke OR immediately on blur. Skips when
   * email is empty / invalid. Result lives in add_email_existing_pool_ids
   * which drives the picker's disabled-state.
   */
  on_add_user_email_change() {
    if (this._add_email_lookup_timer) clearTimeout(this._add_email_lookup_timer);
    this._add_email_lookup_timer = setTimeout(() => this._run_add_email_lookup(), 350);
  }
  async _run_add_email_lookup() {
    const email = String(this.newUser?.email || '').trim();
    if (!email || !this.is_valid_email(email)) {
      this.add_email_existing_pool_ids = [];
      return;
    }
    this.add_email_lookup_busy = true;
    try {
      const pools = await this.adminService.lookupPoolsForEmail(email);
      this.add_email_existing_pool_ids = Array.isArray(pools) ? pools : [];
    } finally {
      this.add_email_lookup_busy = false;
    }
  }
  is_add_all_scanners_checked(group: any): boolean {
    return this.selected_add_scanners.includes(group.wildcard);
  }
  on_add_all_scanners_change(group: any, event: any) {
    const checked = event.target.checked;
    const ids = group.scanners.map((s: any) => s.Id);
    if (checked) {
      // Drop concrete IDs from this group, then add the wildcard token.
      this.selected_add_scanners = this.selected_add_scanners.filter((sid: string) => !ids.includes(sid));
      if (!this.selected_add_scanners.includes(group.wildcard)) {
        this.selected_add_scanners.push(group.wildcard);
      }
    } else {
      this.selected_add_scanners = this.selected_add_scanners.filter((sid: string) => sid !== group.wildcard);
    }
  }
  is_add_scanner_checked(scannerId: string, group: any): boolean {
    if (this.selected_add_scanners.includes(group.wildcard)) return true;
    return this.selected_add_scanners.includes(scannerId);
  }
  is_add_scanner_disabled(group: any): boolean {
    return this.selected_add_scanners.includes(group.wildcard);
  }
  on_add_scanner_change(scannerId: string, event: any) {
    const checked = event.target.checked;
    if (checked) {
      if (!this.selected_add_scanners.includes(scannerId)) {
        this.selected_add_scanners.push(scannerId);
      }
      // IT-F2-106 Phase 3a: auto-expand the Channels sub-panel when
      // enabling a scanner that declares channels[]. Claims auto-expand
      // was removed with the Phase 3b revert — Claims use the modal
      // launcher again for Add-User until an inline design that
      // doesn't freeze on grouped-array schemas ships.
      if (this.addUserScannerChannels(scannerId).length > 0) {
        this._addUserExpandedChannelsFor.add(scannerId);
      }
    } else {
      this.selected_add_scanners = this.selected_add_scanners.filter((sid: string) => sid !== scannerId);
      // Drop the per-scanner channel picks + collapse the sub-panel so
      // a re-enable starts fresh rather than resurrecting stale picks.
      this._addUserPerScannerChannels.delete(scannerId);
      this._addUserExpandedChannelsFor.delete(scannerId);
    }
  }
  /**
   * RFC-5322-ish email check — same shape Cognito's username-as-email
   * pools enforce. Backend AdminCreateUser bounces "Username should
   * be an email" otherwise; cheaper to gate the Save button.
   */
  is_valid_email(s: string): boolean {
    if (!s) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s).trim());
  }
  can_create_user() {
    let u = this.newUser;
    if (!u.email || !u.role) {
      return false;
    }
    // IT-F2-407: name / last_name only required when admin is setting an
    // inline password (data.password provided). On the magic-link /
    // welcome-email path the user fills their own name on the
    // /accept-invite set-password page (f2-members enforces required there).
    const inlinePasswordPath = !!(u.password && String(u.password).length > 0);
    if (inlinePasswordPath) {
      if (!u.name || !u.last_name) return false;
    }
    if (!this.is_valid_email(u.email)) {
      return false;
    }
    if (u.role !== 'admin') {
      // Pool radio must be picked (null = untouched; '' = legacy is a
      // valid explicit pick). Then at least one slug within that pool
      // AND at least one scanner selection.
      if (this.add_selected_pool_id === null) return false;
      if (this.selected_add_customers.length === 0) return false;
      if (this.selected_add_scanners.length === 0) return false;
    }
    return true;
  }
  async on_create_user() {
    const email = this.newUser.email;
    const sentMagicLink = this.newUser.send_magic_link === true;

    // Admin role bypasses the per-pool fan-out — admin identities live
    // only in the legacy pool and carry '*' for customers/scanners (the
    // f2-auth-service2 admin expansion handles entitlement at runtime).
    if (this.newUser.role === 'admin') {
      this.newUser.customers = '*';
      this.newUser.scanners = '*';
      // Reset the modal queue so this create's outcome isn't confused
      // with a prior submit's.
      this.pendingLegacyMagicLinks = [];
      try {
        // Admin identities always land in the legacy pool → force
        // send_magic_link=false (legacy has no branded SES sender, per
        // ER-4533V6F + ER-K6S84R5). Post-create pop the copy-magic-link
        // modal so the admin can hand the URL over out-of-band.
        const adminPayload = { ...this.newUser, send_magic_link: false };
        const res = await this.adminService.createUser(adminPayload);
        if (res && !res.err) {
          this.data.push(res);
          try { this.grid?.api?.setGridOption('rowData', this.data); } catch (e: any) {
            console.warn('setGridOption after admin create failed:', e?.message);
          }
          this.toastService.success('Admin user created', `${email} — legacy pool. Copy the magic-link below to send.`);
          this.pendingLegacyMagicLinks.push({
            email,
            username: String(res.username || res.attributes?.email || email),
            pool_id: undefined,
            pool_label: 'legacy · admin (all customers)',
            copied: false,
            busy: false,
          });
        }
      } catch (e: any) {
        // Belt-and-braces: adminService.createUser already catches +
        // surfaces its own toast, but if anything in the post-create
        // grid update / toast path throws we still need to close the
        // modal cleanly. Without this, Mike saw the modal stick with
        // 'Creating user…' overlay on a successful backend create for
        // alpha-shark, needing a page refresh to recover. 2026-07-01.
        console.warn('Admin create post-processing failed:', e?.message);
      }
      this.showAddUserModal = false;
      if (this.pendingLegacyMagicLinks.length > 0) {
        this.showLegacyMagicLinkModal = true;
      }
      return;
    }

    // Member-role flow: group the selected customer slugs by their
    // resolved Cognito pool (legacy = null), then issue one
    // POST /admin/users per distinct pool with the slug subset that
    // belongs there. This produces one identity per pool, each with
    // custom:customers scoped to that pool's slugs — matching the
    // multi-pool user contract ("one entry per pool, potentially
    // different passwords"). Mike 2026-06-26.
    const groups = this.group_selected_customers_by_pool();
    if (groups.length === 0) {
      this.toastService.error('No customers', 'Pick at least one customer before creating the user.');
      return;
    }

    // Guard: refuse to submit any group whose pool already has this
    // email. The picker should have disabled those checkboxes, but
    // defend in case the lookup hadn't resolved before submit.
    const blocked = this.selected_taken_slugs();
    if (blocked.length) {
      this.toastService.error(
        'Already exists',
        `User ${email} already exists in the pool(s) for: ${blocked.join(', ')}. Uncheck those customers (or pick others) and try again.`,
      );
      return;
    }

    // Wrap the member-role create+post-processing block in try/finally
    // so a throw in setGridOption / toast / anywhere else NEVER strands
    // the operator with the modal open on top of an already-successful
    // backend create. Mike 2026-07-01: alpha-shark client-user creation
    // was hanging on the modal until refresh; the backend had created
    // the user, but a post-response exception blocked the modal close.
    try {
    // Per-scanner claims + notify_channels are baked into the CREATE
    // payload as data.claims / data.notify_channels — backend adds
    // them to attrs[] before AdminCreateUser, so client-role operators
    // can set these at Add User time without needing a separate
    // updateUserAttributes call (which the admin-only Update gate was
    // rejecting for client-role callers). Same JSON shape the shared
    // claims-editor / channels-editor emit. Mike 2026-07-02.
    const claimsJson = String(this.add_user_claims_json || '').trim();
    const channelsJson = String(this.add_user_channels_json || '').trim();

    const results: Array<{ pool_id: string | null; slugs: string[]; res: any }> = [];
    // Reset any leftover queue from a prior submit so the modal only
    // reflects THIS create's legacy successes.
    this.pendingLegacyMagicLinks = [];
    for (const g of groups) {
      // Per-group scanners: keep only the entries that match the
      // group's slugs OR wildcards derived from them. This avoids
      // bleeding one customer's scanner IDs into the other pool's
      // identity.
      const groupSlugSet = new Set(g.slugs);
      const groupScannerIds = new Set(
        this.scanners.filter((s: any) => groupSlugSet.has(s.Client)).map((s: any) => String(s.Id || '')),
      );
      const groupWildcards = new Set<string>();
      for (const slug of g.slugs) {
        const scs = this.scanners.filter((s: any) => s.Client === slug);
        const ids = scs.map((s: any) => s.Id);
        const prefix = this._longest_common_prefix(ids);
        groupWildcards.add(prefix ? `${prefix}*` : `${slug}-*`);
      }
      const groupScanners = (this.selected_add_scanners || []).filter((sid: string) => {
        return groupScannerIds.has(sid) || groupWildcards.has(sid);
      });

      const payload: any = {
        ...this.newUser,
        customers: g.slugs.join(','),
        scanners: groupScanners.join(','),
      };
      // Explicit pool_id when per-customer; omit (undefined) for legacy
      // so the backend's existing "no pool_id → legacy" path runs.
      if (g.pool_id) payload.pool_id = g.pool_id;
      // Force send_magic_link=false for the legacy pool — SES sending
      // is not configured for it and the backend refuses to mint into
      // the Cognito-default sender (would look like phishing —
      // ER-4533V6F). We handle legacy-pool magic-link handoff via the
      // post-create modal instead.
      if (!g.pool_id) payload.send_magic_link = false;
      if (claimsJson) payload.claims = claimsJson;
      if (channelsJson) payload.notify_channels = channelsJson;

      const res = await this.adminService.createUser(payload);
      results.push({ pool_id: g.pool_id, slugs: g.slugs, res });
      if (res && !res.err) {
        this.data.push(res);
        // Legacy-pool success → queue for the copy-magic-link modal so
        // the admin can hand the URL to the new user out-of-band.
        if (!g.pool_id) {
          this.pendingLegacyMagicLinks.push({
            email,
            username: String(res.username || res.attributes?.email || email),
            pool_id: undefined,
            pool_label: `legacy · ${g.slugs.join(', ')}`,
            copied: false,
            busy: false,
          });
        }
      }
    }
    try { this.grid?.api?.setGridOption('rowData', this.data); } catch (e: any) {
      // Grid might not be ready if the operator opened the modal
      // during initial load or the grid was destroyed. Non-fatal —
      // getUsers() below repopulates.
      console.warn('setGridOption after member create failed:', e?.message);
    }

    // Aggregate toast: how many pools succeeded, which failed.
    const ok = results.filter(r => r.res && !r.res.err);
    const fail = results.filter(r => !r.res || r.res.err);
    if (ok.length && !fail.length) {
      const poolsLabel = ok.length === 1 ? 'pool' : 'pools';
      const magicSent = ok.every(r => (r.res as any)?.magic_link_sent === true)
        || (sentMagicLink && ok.every(r => (r.res as any)?.magic_link_sent !== false));
      if (magicSent) {
        this.toastService.success(
          'User created',
          `${email} — created in ${ok.length} ${poolsLabel}. Magic-link invite sent (7-day TTL).`,
        );
      } else {
        this.toastService.success(
          'User created',
          `${email} — created in ${ok.length} ${poolsLabel}. Cognito will email a temporary password.`,
        );
      }
    } else if (ok.length && fail.length) {
      this.toastService.error(
        'Partial success',
        `${ok.length} pool(s) created; ${fail.length} failed: ${fail.map(f => f.slugs.join(',')).join(' | ')}.`,
      );
    }
    // ok.length === 0 → adminService.createUser already surfaced the per-call error toast.
    } catch (postErr: any) {
      // Never leave the modal stuck. adminService.createUser already
      // handles its own loading-service remove + toast; this branch
      // covers exceptions in the OUTER orchestration (grid update,
      // toast composition, etc).
      console.warn('Member create post-processing failed:', postErr?.message);
    } finally {
      this.showAddUserModal = false;
      // If any legacy-pool creates succeeded, pop the copy-magic-link
      // modal so the admin can hand the URL to the user out-of-band
      // (legacy pool has no branded SES sender — see ER-4533V6F).
      if (this.pendingLegacyMagicLinks.length > 0) {
        this.showLegacyMagicLinkModal = true;
      }
    }
  }

  /**
   * Mint the magic-link URL for one queued legacy-pool user and copy it
   * to the clipboard. Sets a per-row copied flag so the modal can render
   * a "✓ Copied" chip and remind the operator which rows are still
   * pending.
   */
  async copy_legacy_magic_link(entry: (typeof this.pendingLegacyMagicLinks)[number]) {
    if (entry.busy) return;
    entry.busy = true;
    try {
      const res = await this.adminService.mintMagicLink(entry.username, entry.pool_id);
      if (res?.err || !res?.url) {
        // adminService.mintMagicLink surfaces its own toast on failure.
        return;
      }
      this.copyText(res.url, 'Magic-link URL');
      entry.copied = true;
    } finally {
      entry.busy = false;
    }
  }

  close_legacy_magic_link_modal() {
    this.showLegacyMagicLinkModal = false;
    this.pendingLegacyMagicLinks = [];
  }

  async on_sync_user_click() {
    if (!this.selected_user) return;
    let res = await this.adminService.syncUser(this.selected_user.username);
    if (res && !res.err && res.username) {
      this.selected_user = res;
    }
  }

  //Delete User
  // client_admin role uses the request-deletion flow instead of direct
  // delete (per project_option_pit_customer_host_migration — only super-
  // admin can actually delete). Template should read is_super_admin_delete()
  // to label the button "Delete" vs "Request Deletion".

  // Targets are the rows the operator wants to delete: prefer the
  // checkbox multi-selection, fall back to the single click-selected
  // row. The current user is always filtered out as a self-protection.
  delete_targets(): any[] {
    const fromCheckboxes = this.selected_users_for_password || [];
    const list = fromCheckboxes.length > 0
      ? fromCheckboxes
      : (this.selected_user ? [this.selected_user] : []);
    const me = this.authService.get_username();
    return list.filter((u: any) => u?.username && u.username !== me);
  }
  delete_target_count(): number {
    return this.delete_targets().length;
  }
  // Modal-shown preview list -- cap at 10 so a bulk-of-thousands
  // doesn't overflow the dialog. Remainder rendered as "...and N more".
  delete_targets_preview(): any[] {
    return this.delete_targets().slice(0, 10);
  }
  delete_targets_overflow(): number {
    return Math.max(0, this.delete_target_count() - 10);
  }
  can_delete_user() {
    return this.delete_target_count() > 0;
  }
  is_super_admin_delete(): boolean {
    return this.authService.get_role() === 'admin';
  }
  on_delete_user_click() {
    this.showDeleteUserModal = true;
  }
  async delete_user(user: any) {
    // Branch on role. Super-admin → actual delete (existing path).
    // client_admin → request-deletion (writes to PendingDeletions, no
    // Cognito delete). Backend enforces both — frontend just picks the
    // right endpoint to keep the UX correct.
    if (this.authService.get_role() === 'client_admin') {
      let res = await this.adminService.requestUserDeletion(user.username);
      if (res?.success) {
        this.toastService.success(
          'Deletion requested',
          'A super-admin will review and approve.'
        );
        this.showDeleteUserModal = false;
      }
      return;
    }
    let res = await this.adminService.deleteUser(user.username, user?.pool_id);
    if (res?.success) {
      //Remove user from grid (match on pool_id + username — compound id,
      //since the same username can appear in multiple pools)
      _.remove(this.data, (u: any) => {
        return u.username === user.username && u.pool_id === user.pool_id;
      });
      this.grid.api.setGridOption("rowData", this.data);
      this.showDeleteUserModal = false;
    }
  }
  // Bulk delete -- loops over delete_targets() and dispatches per-user
  // to the existing single-user delete / request-deletion endpoints.
  // Tracks successes vs failures, removes successful deletes from the
  // grid in one update, clears the multi-selection, and toasts a
  // summary. client_admin → request-deletion path; super-admin →
  // actual delete.
  async delete_selected_users() {
    const targets = this.delete_targets();
    if (targets.length === 0) {
      this.showDeleteUserModal = false;
      return;
    }
    const isClientAdmin = this.authService.get_role() === 'client_admin';
    let success = 0;
    let failed = 0;
    // Compound (pool_id, username) keys — same username can appear in N
    // pools, plain username-match would over-delete from the grid after
    // a partial-success bulk delete.
    const removedRowIds: string[] = [];
    const errors: string[] = [];
    for (const user of targets) {
      try {
        const res: any = isClientAdmin
          ? await this.adminService.requestUserDeletion(user.username)
          : await this.adminService.deleteUser(user.username, user?.pool_id);
        if (res?.success) {
          success++;
          if (!isClientAdmin) removedRowIds.push(`${user.pool_id || ''}::${user.username}`);
        } else {
          failed++;
          if (res?.err) errors.push(`${user?.attributes?.email || user.username} [${user?.customer_slug || 'unknown'}]: ${res.err}`);
        }
      } catch (e: any) {
        failed++;
        errors.push(`${user?.attributes?.email || user.username} [${user?.customer_slug || 'unknown'}]: ${e?.message || 'unknown'}`);
      }
    }
    if (removedRowIds.length > 0) {
      _.remove(this.data, (u: any) => removedRowIds.includes(`${u.pool_id || ''}::${u.username}`));
      this.grid.api.setGridOption('rowData', this.data);
    }
    this.selected_users_for_password = [];
    this.selected_user = null;
    this.showDeleteUserModal = false;
    if (failed > 0) {
      this.toastService.error(
        `${failed} delete(s) failed`,
        `${success} succeeded. ${errors.slice(0, 3).join(' | ')}${errors.length > 3 ? '…' : ''}`,
      );
    } else {
      this.toastService.success(
        isClientAdmin ? 'Deletion requested' : 'Users deleted',
        `${success} user(s) ${isClientAdmin ? 'queued for review' : 'removed'}`,
      );
    }
  }

  //Edit User
  async on_user_edit(event: any) {
    const node = event.node;
    const oldValue = event.oldValue;
    const newValue = event.newValue;
    const colId = event.column.colId;

    if (oldValue === newValue) return;

    switch(colId) {
      case 'attributes.name':
      case 'attributes.family_name':
      case 'attributes.custom:role':
      case 'attributes.custom:comments':
      case 'attributes.custom:scanners':
      case 'attributes.custom:claims':
      case 'attributes.custom:notify_channels': {
        let attr_name = colId.replace('attributes.', '');
        let data = {
          attributes: [
            {
              Name: attr_name,
              Value: newValue
            }
          ]};
        let res = await this.adminService.updateUserAttributes(node.data.username, data, node.data?.pool_id);
        if (res?.err) {
          //Revert the change
          node.data.attributes[attr_name] = oldValue;
          node.setData(node.data);
        }
        break;
      }

      case 'attributes.custom:live_data_access': {
        let res = await this.adminService.updateUserLiveAccess(node.data.username, newValue);
        if (res?.err) {
          //Revert the change
          node.data.attributes['custom:live_data_access'] = oldValue;
          node.setData(node.data);
        }
        break;
      }

      case 'attributes.custom:agreement_nasdaq':
      case 'attributes.custom:agreement_nyse':
      case 'attributes.custom:agreement_opra': {
        let attr_name = colId.replace('attributes.', '');
        let data = {
          attributes: [
            {
              Name: attr_name,
              Value: newValue.toString()
            }
          ]};
        let res = await this.adminService.updateUserAttributes(node.data.username, data, node.data?.pool_id);
        if (res?.err) {
          //Revert the change
          node.data.attributes[attr_name] = oldValue;
          node.setData(node.data);
        }
        break;
      }

      case 'enabled': {
        let res = await this.adminService.enableUser(node.data.username, newValue, node.data?.pool_id);
        if (res?.err) {
          //Revert the change
          node.data.enabled = oldValue;
          node.setData(node.data);
        }
        break;
      }
      
      default:
        break;
    }
  }
  on_edit_user_scanners(user: any) {
    let scanners = user.attributes['custom:scanners'] || '';
    // Filter out empty strings from the split so checkbox state isn't polluted.
    this.selected_user_scanners = scanners.split(',').filter((s: string) => s && s.trim() !== '');
    this.showEditUserScannersModal = true;
  }
  // All scanners grouped by Client, used to render the Edit-Scanners modal
  // with one wildcard-checkbox + scanner-checkboxes block per customer.
  get all_scanner_groups(): { customer: string; prefix: string; wildcard: string; scanners: any[] }[] {
    const by_client: Record<string, any[]> = {};
    for (const s of this.scanners) {
      const c = s?.Client;
      if (!c) continue;
      if (!by_client[c]) by_client[c] = [];
      by_client[c].push(s);
    }
    return Object.keys(by_client).sort().map((c) => {
      const scs = by_client[c];
      const prefix = this._longest_common_prefix(scs.map((s: any) => s.Id));
      const wildcard = prefix ? `${prefix}*` : `${c}-*`;
      return { customer: c, prefix, wildcard, scanners: scs };
    });
  }
  is_edit_all_scanners_checked(group: any): boolean {
    return this.selected_user_scanners.includes(group.wildcard);
  }
  on_edit_all_scanners_change(group: any, event: any) {
    const checked = event.target.checked;
    const ids = group.scanners.map((s: any) => s.Id);
    if (checked) {
      this.selected_user_scanners = this.selected_user_scanners.filter((sid: string) => !ids.includes(sid));
      if (!this.selected_user_scanners.includes(group.wildcard)) {
        this.selected_user_scanners.push(group.wildcard);
      }
    } else {
      this.selected_user_scanners = this.selected_user_scanners.filter((sid: string) => sid !== group.wildcard);
    }
  }
  is_edit_scanner_checked(scannerId: string, group: any): boolean {
    if (this.selected_user_scanners.includes(group.wildcard)) return true;
    return this.selected_user_scanners.includes(scannerId);
  }
  is_edit_scanner_disabled(group: any): boolean {
    return this.selected_user_scanners.includes(group.wildcard);
  }
  on_edit_user_customers(user: any) {
    const csv = user.attributes['custom:customers'] || '';
    this.selected_user_customers = csv.split(',').map((s: string) => s.trim()).filter(Boolean);
    // Force-include the row's own pool slug so the locked checkbox
    // renders as CHECKED even when the user's existing
    // custom:customers doesn't carry it (defensive — a per-customer-
    // pool user that wasn't seeded with their own slug in
    // custom:customers would otherwise show an unchecked-but-disabled
    // box, which is confusing).
    const slug = String(user?.customer_slug || '').trim().toLowerCase();
    if (slug && slug !== 'f2' && !this.selected_user_customers.includes(slug)) {
      this.selected_user_customers.push(slug);
    }
    this.showEditUserCustomersModal = true;
  }

  // Resolve a (possibly-legacy) customer slug to the canonical _id from
  // F2-ADMIN.Customers via the aliases[] field. Returns the input
  // unchanged when no directory row matches by _id or alias — keeps
  // truly-unknown slugs visible instead of silently rewriting them.
  // Used wherever the UI key needs to track the canonical slug
  // (override-password lookup, future per-customer keyed maps).
  _normalize_slug(slug: string): string {
    const s = String(slug || '').trim().toLowerCase();
    if (!s) return s;
    for (const c of this.directoryCustomers || []) {
      if (String(c?._id || '').toLowerCase() === s) return String(c._id);
      const aliases: string[] = Array.isArray(c?.aliases) ? c.aliases : [];
      if (aliases.some((a) => String(a || '').toLowerCase() === s)) return String(c._id);
    }
    return s;
  }

  // Display name for a slug — customer.name from F2-ADMIN.Customers
  // when the slug (or one of its aliases) resolves; otherwise the raw
  // slug. Powers the per-row label in the Override-Password modal so
  // operators see "HIMA Reddy" instead of "hima" / "hima-reddy".
  _customer_name_for_slug(slug: string): string {
    const s = String(slug || '').trim().toLowerCase();
    if (!s) return slug;
    for (const c of this.directoryCustomers || []) {
      const id = String(c?._id || '').toLowerCase();
      const aliases: string[] = Array.isArray(c?.aliases) ? c.aliases.map((a: any) => String(a || '').toLowerCase()) : [];
      if (id === s || aliases.includes(s)) return String(c?.name || c?._id || slug);
    }
    return slug;
  }

  // True when the directory has no row matching the slug by _id or
  // alias — drives the amber "unrecognized" hint in the
  // Override-Password modal (mirrors the Customers-assignment popup
  // behavior so legacy / typo / withdrawn slugs aren't invisible).
  _is_unrecognized_slug(slug: string): boolean {
    const s = String(slug || '').trim().toLowerCase();
    if (!s) return false;
    for (const c of this.directoryCustomers || []) {
      const id = String(c?._id || '').toLowerCase();
      const aliases: string[] = Array.isArray(c?.aliases) ? c.aliases.map((a: any) => String(a || '').toLowerCase()) : [];
      if (id === s || aliases.includes(s)) return false;
    }
    return true;
  }

  // True when the user's selected_user_customers contains either the
  // customer's canonical _id OR any of its registered aliases (Mongo
  // F2-ADMIN.Customers.aliases[]). Drives the checkbox state in the
  // Customers assignment modal so a user still carrying a legacy slug
  // (e.g. 'hima' for what is now 'hima-reddy') reads as "assigned"
  // against the canonical row, not as an orphan in the unrecognized
  // section below.
  is_customer_assigned(customer: any): boolean {
    const id = String(customer?._id || '');
    if (!id) return false;
    if (this.selected_user_customers.includes(id)) return true;
    const aliases: string[] = Array.isArray(customer?.aliases) ? customer.aliases : [];
    return aliases.some((a) => this.selected_user_customers.includes(a));
  }

  // True when this customer slug is the row's OWN per-customer pool slug
  // (e.g. row from option-pit pool → 'option-pit' checkbox must stay
  // locked ON). Unchecking would orphan the user from the customer
  // whose Cognito pool they actually live in — they'd lose access to
  // their own product surface. Only applies to non-legacy rows;
  // legacy ('f2') rows have no such constraint because legacy isn't
  // a customer slug.
  is_customer_lock_required(customer: any): boolean {
    const slug = String(this.selected_user?.customer_slug || '').trim().toLowerCase();
    if (!slug || slug === 'f2') return false;
    const id = String(customer?._id || '').toLowerCase();
    if (id === slug) return true;
    const aliases: string[] = Array.isArray(customer?.aliases) ? customer.aliases : [];
    return aliases.some((a) => String(a || '').toLowerCase() === slug);
  }

  // True when this customer has its own Cognito pool AND adding the slug
  // here would create an out-of-pool claim. The Customers picker should
  // only let an admin GRANT a customer's slug from within that customer's
  // own pool (so the user lands in the right pool with seeded data); this
  // is a no-grant gate for any LEGACY-row attempt to bolt a managed slug
  // on. Per Mike 2026-06-27: "customers that have their own cognito pool
  // shouldn't be enablable outside of their pool from this UX however
  // they can be disabled if they erroneously exist".
  //
  // Returns false (i.e. NOT blocked) when:
  //   - customer has no cognito_pool                → legacy-only, free to grant
  //   - row IS in this customer's pool              → handled by
  //                                                   is_customer_lock_required (locked ON)
  //   - slug is ALREADY assigned (the erroneous-claim case) → must stay
  //                                                   enabled so admin can uncheck it
  is_customer_add_blocked(customer: any): boolean {
    if (!customer?.cognito_pool?.user_pool_id) return false;
    if (this.is_customer_lock_required(customer)) return false;
    if (this.is_customer_assigned(customer)) return false;
    return true;
  }

  // Toggle a Customer assignment. Always writes the CANONICAL _id when
  // checked + strips any of the customer's aliases from the selection
  // (so saving converges the user's CSV to the source-of-truth slug
  // even if they were originally assigned via a legacy alias). On
  // uncheck, removes both the canonical and every alias.
  on_customer_toggle(customer: any, event: any) {
    const id = String(customer?._id || '');
    if (!id) return;
    const aliases: string[] = Array.isArray(customer?.aliases) ? customer.aliases : [];
    const selected = event?.target?.checked;
    // Drop the canonical + every alias from the selection in either
    // branch — we rebuild the membership state from scratch below.
    this.selected_user_customers = this.selected_user_customers.filter(
      (s: string) => s !== id && !aliases.includes(s),
    );
    if (selected) this.selected_user_customers.push(id);
  }

  // Slugs the user has in custom:customers that match no directory
  // Customer (either by _id or alias). Surfaced in the modal so admins
  // can see (and remove) legacy / typo / withdrawn customer slugs that
  // would otherwise be invisible. Always-checked by default; admin
  // unchecks to drop on save.
  get unrecognized_user_slugs(): string[] {
    const known = new Set<string>();
    for (const c of this.directoryCustomers || []) {
      if (c?._id) known.add(String(c._id));
      if (Array.isArray(c?.aliases)) {
        for (const a of c.aliases) if (a) known.add(String(a));
      }
    }
    return (this.selected_user_customers || []).filter((s: string) => s && !known.has(s));
  }

  on_unrecognized_slug_toggle(slug: string, event: any) {
    const selected = event?.target?.checked;
    if (!selected) {
      this.selected_user_customers = this.selected_user_customers.filter((s: string) => s !== slug);
    } else if (!this.selected_user_customers.includes(slug)) {
      this.selected_user_customers.push(slug);
    }
  }
  on_user_scanner_change(scanner: any, event: any) {
    // console.log('scanner change', scanner, event);
    // console.log(scanner.Name, event.target.checked);
    let selected = event.target.checked;
    if (selected) {
      this.selected_user_scanners.push(scanner.Id);
    }
    else {
      _.remove(this.selected_user_scanners, (s: any) => {
        return s === scanner.Id;
      });
    }
  }
  on_user_customer_change(customer: any, event: any) {
    let selected = event.target.checked;
    if (selected) {
      this.selected_user_customers.push(customer);
    }
    else {
      _.remove(this.selected_user_customers, (c: any) => {
        return c === customer;
      });
    }
  }
  async update_user_scanners() {
    // console.log('todo update_user_scanners() => ', this.selected_user);
    // console.log(this.selected_user_scanners);
    let val: string[] = [];
    _.each(this.selected_user_scanners, (s: any) => {
      if (!s) return;
      if (val.indexOf(s) === -1) {
        val.push(s);
      }
    });

    let data = {
      attributes: [
        {
          Name: 'custom:scanners',
          Value: val.join(',')
        }
      ]};
    let res = await this.adminService.updateUserAttributes(this.selected_user.username, data, this.selected_user?.pool_id);
    if (res && !res.err) {
      //Update the node in the grid (compound rowId: pool::username)
      this.selected_user.attributes['custom:scanners'] = val.join(',');
      let node = this.grid.api.getRowNode(`${this.selected_user?.pool_id || ''}::${this.selected_user.username}`);
      if (node) {
        node.setData(this.selected_user);
        this.showEditUserScannersModal = false;
      }
    }
  }
  async update_user_customers() {
    let val: string[] = [];
    _.each(this.selected_user_customers, (c: any) => {
      if (!c) return;
      if (val.indexOf(c) === -1) {
        val.push(c);
      }
    });

    let data = {
      attributes: [
        {
          Name: 'custom:customers',
          Value: val.join(',')
        }
      ]};
    let res = await this.adminService.updateUserAttributes(this.selected_user.username, data, this.selected_user?.pool_id);
    if (res && !res.err) {
      //Update the node in the grid (compound rowId: pool::username)
      this.selected_user.attributes['custom:customers'] = val.join(',');
      let node = this.grid.api.getRowNode(`${this.selected_user?.pool_id || ''}::${this.selected_user.username}`);
      if (node) {
        node.setData(this.selected_user);
        this.showEditUserCustomersModal = false;
      }
    }
  }

  // ----- Claims editor wiring -----
  // The ClaimsEditorComponent (shared/claims-editor/) is reusable. These
  // methods bridge it to the grid: stash the user being edited, hand off
  // their current claims JSON, and write back on save.

  on_edit_user_claims(user: any) {
    this.selected_user_for_claims = user;
    this.selected_user_claims_json = String(user?.attributes?.['custom:claims'] || '');
    this.showEditUserClaimsModal = true;
  }

  async on_save_user_claims(newJson: string) {
    const user = this.selected_user_for_claims;
    if (!user) { this.showEditUserClaimsModal = false; return; }
    const data = {
      attributes: [{ Name: 'custom:claims', Value: newJson }],
    };
    const res = await this.adminService.updateUserAttributes(user.username, data, user?.pool_id);
    if (res && !res.err) {
      user.attributes['custom:claims'] = newJson;
      const node = this.grid.api.getRowNode(`${user?.pool_id || ''}::${user.username}`);
      if (node) node.setData(user);
      this.showEditUserClaimsModal = false;
      this.selected_user_for_claims = null;
    }
  }

  // ----- Channels editor wiring (mirrors claims) -----
  // The Cognito attribute is `custom:notify_channels` (NOT `custom:channels`).
  // The wrong key was wired in initially and shipped — every read returned
  // undefined (column blank), every save would have errored on Cognito schema
  // (the misspelled attribute isn't declared on the pool). Zero leaked writes
  // confirmed in the 2026-06-17 backup (0 occurrences of custom:channels);
  // 5/5 sampled live users still carry the correct custom:notify_channels
  // values matching the backup. No data restoration needed — UI fix only.
  on_edit_user_channels(user: any) {
    this.selected_user_for_channels = user;
    this.selected_user_channels_json = String(user?.attributes?.['custom:notify_channels'] || '');
    this.showEditUserChannelsModal = true;
  }

  async on_save_user_channels(newJson: string) {
    const user = this.selected_user_for_channels;
    if (!user) { this.showEditUserChannelsModal = false; return; }
    const data = {
      attributes: [{ Name: 'custom:notify_channels', Value: newJson }],
    };
    const res = await this.adminService.updateUserAttributes(user.username, data, user?.pool_id);
    if (res && !res.err) {
      user.attributes['custom:notify_channels'] = newJson;
      const node = this.grid.api.getRowNode(`${user?.pool_id || ''}::${user.username}`);
      if (node) node.setData(user);
      this.showEditUserChannelsModal = false;
      this.selected_user_for_channels = null;
    }
  }

  // IT-F2-406 cid d97cf968 — admin-side companion to on_save_user_channels.
  // When the Edit Channels modal's role-visibility chips were touched, the
  // editor emits one entry per scanner with the FULL merged channels[]
  // array. PATCH each scanner via scannersService; mirror the change into
  // the local in-memory this.scanners catalog so a subsequent save of the
  // SAME modal doesn't re-prompt. Failures bubble up through the service's
  // toast; successful scanners are patched regardless of per-scanner order.
  async on_save_scanner_channels(updates: Array<{ scanner_id: string; channels: any[] }>) {
    if (!Array.isArray(updates) || updates.length === 0) return;
    for (const u of updates) {
      if (!u?.scanner_id || !Array.isArray(u.channels)) continue;
      const res = await this.scannersService.updateScanner(u.scanner_id, { channels: u.channels });
      if (res && !res.err) {
        const idx = (this.scanners || []).findIndex((s: any) => String(s._id || s.Id || '') === u.scanner_id);
        if (idx >= 0) this.scanners[idx] = { ...this.scanners[idx], channels: u.channels };
      }
    }
  }

  // ----- Unified scanner-access editor wiring -----
  // Drives BOTH custom:customers and custom:scanners in one save so
  // the two co-dependent attributes can't drift. The dialog also
  // hides pool-mismatched customers (a user in pool A can't be granted
  // access to a customer in pool B without re-provisioning), and
  // preserves any opaque scanner tokens it can't classify as orphans.
  on_edit_user_access(user: any) {
    this.selected_user_for_access = user;
    this.showEditUserAccessModal = true;
  }

  async on_save_user_access(payload: { customers: string; scanners: string }) {
    const user = this.selected_user_for_access;
    if (!user) { this.showEditUserAccessModal = false; return; }
    const data = {
      attributes: [
        { Name: 'custom:customers', Value: payload.customers },
        { Name: 'custom:scanners',  Value: payload.scanners },
      ],
    };
    this.savingUserAccess = true;
    try {
      const res = await this.adminService.updateUserAttributes(user.username, data, user?.pool_id);
      if (res && !res.err) {
        user.attributes['custom:customers'] = payload.customers;
        user.attributes['custom:scanners'] = payload.scanners;
        const node = this.grid.api.getRowNode(`${user?.pool_id || ''}::${user.username}`);
        if (node) node.setData(user);
        this.showEditUserAccessModal = false;
        this.selected_user_for_access = null;
      } else {
        // Surface the error so the operator KNOWS it failed; previously
        // the modal stayed silently open on failure with no signal.
        const msg = (res && res.err) || 'Update failed';
        this.toastService.error('Edit Access save failed', msg);
      }
    } finally {
      this.savingUserAccess = false;
    }
  }
  savingUserAccess = false;

  // One-line summary the cellRenderer shows when the row isn't selected
  // (and on the hover tooltip when it is). Walks the parsed entries and
  // emits something like "strat-alerts:10, mta:tier2, tt-uoa:live=true".
  _summarize_claims(raw: any): string {
    const s = String(raw || '').trim();
    if (!s) return '—';
    let arr: any;
    try { arr = JSON.parse(s); } catch { return '(invalid JSON)'; }
    if (!Array.isArray(arr) || !arr.length) return '—';
    const parts: string[] = [];
    for (const e of arr) {
      if (!e || typeof e !== 'object') continue;
      const sc = String(e.scanner || '?');
      // Pick the first non-scanner field as the most informative summary.
      let tag = '';
      for (const k of Object.keys(e)) {
        if (k === 'scanner') continue;
        const v = e[k];
        if (Array.isArray(v)) { tag = `${k}=[${v.length}]`; break; }
        if (typeof v === 'object' && v !== null) continue;
        tag = `${k}=${v}`;
        break;
      }
      parts.push(tag ? `${sc}:${tag}` : sc);
    }
    return parts.join(', ');
  }

  // Channels summary — Cognito custom:notify_channels is a flat CSV of
  // channel IDs (e.g. "as-1,as-7,as-12"), NOT the JSON-array shape that
  // claims uses. The previous aliasing to _summarize_claims rendered every
  // row as "(invalid JSON)" once the column was correctly wired up.
  // Tolerates both shapes for forward-compat: a JSON array falls through
  // to the claims-style summary (would happen if a customer migrates to
  // a schema-driven channels model), and anything else is treated as
  // comma/space/semicolon-separated channel IDs.
  _summarize_channels(raw: any): string {
    const s = String(raw || '').trim();
    if (!s) return '—';
    // Try JSON first — opens a future path where channels become schema-
    // driven without breaking the column.
    if (s.startsWith('[') || s.startsWith('{')) {
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) return this._summarize_claims(s);
      } catch { /* fall through to CSV */ }
    }
    // CSV path — split on , ; or whitespace, trim, drop blanks.
    const ids = s.split(/[,;\s]+/).map(t => t.trim()).filter(Boolean);
    if (!ids.length) return '—';
    return ids.join(', ');
  }

  _html_escape(s: any): string {
    return String(s ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  //Import Users
  on_import_users_click() {
    this.usersFileImport.nativeElement.value = '';
    this.usersFileImport.nativeElement.click();
  }

  // IT-F2-407 (Mike 2026-09-18): CSV import gains a field-mapping wizard.
  // Old behavior hard-mapped Email/FirstName/LastName by header name —
  // any CSV without those exact headers (or with none at all) failed. Now
  // the file drops into a mapping dialog: preview of first rows, per-
  // column dropdown (Email required; First/Last/Role/Scanners optional;
  // Ignore for skipped columns), header-row toggle (auto-detected on load).
  // Only Email is a hard requirement; missing First/Last routes through
  // the magic-link path so the invitee supplies their own name at
  // /accept-invite set-password time.
  // IT-F2-407 (Mike 2026-09-18): Customer is a valid per-column mapping too
  // (CSV row can carry per-user customer slug). Batch-level default pickers
  // in the modal cover the common case where all rows land in one customer.
  readonly CSV_FIELD_OPTIONS = ['Ignore', 'Email', 'First Name', 'Last Name', 'Role', 'Customer', 'Scanners'] as const;
  csvMapDialogOpen = false;
  csvMapRawRows: string[][] = [];       // raw string arrays, header included when present
  csvMapHasHeader = true;
  csvMapColumnMapping: string[] = [];   // parallel to columns[0..n-1] — 'Email' | 'First Name' | ...
  // IT-F2-407 (Mike 2026-09-18): batch-level customer + scanners for the
  // whole CSV import. Customer picks the pool (backend derives pool_id
  // from customer_slug's cognito_pool.user_pool_id, falling back to
  // legacy pool when absent). Scanners multi-select is filtered to that
  // customer's scanner catalog. Both applied to every row that gets
  // created — per-row overrides are out of scope for MVP.
  csvMapCustomer: string = '';
  csvMapScanners: string[] = [];
  csvMapImporting = false;
  csvMapImportProgress = 0;
  csvMapImportTotal = 0;

  // IT-F2-407 (Mike 2026-09-18 c/6c058572): inline pre-flight so preview
  // rows show an "already exists in target pool" badge BEFORE the operator
  // hits Import. Populated by _run_preview_preflight() when the customer
  // picker changes or the header toggle flips (the Email-column identity
  // depends on both). Set of lowercased emails.
  csvMapExistingInPool = new Set<string>();
  csvMapPreflightRunning = false;
  csvMapPreflightForPool: string = '';    // pool this set was computed against; cache-bust key
  private _csvMapPreflightSeq = 0;

  // IT-F2-407 (Mike 2026-10-20 cid f9d728a2) — per-existing-user metadata
  // so the preview grid can tell "exists with full entitlements (skip)"
  // apart from "exists but is missing N of the selected scanners/customer
  // (will PATCH their custom:scanners / custom:customers)". Keyed by
  // lowercased email, populated alongside csvMapExistingInPool.
  csvMapExistingUsersMeta = new Map<string, {
    username: string;
    pool_id: string;
    scanners: Set<string>;
    customers: Set<string>;
  }>();

  // IT-F2-407 (Mike 2026-09-18 c/2f26d0db): when true, Confirm also fires
  // a password-reset email (Cognito's forgot-password flow) for every row
  // flagged as already existing in the target pool. "there's probably a
  // reason their email is there" — bulk imports frequently double as
  // reactivation of dormant/existing users.
  csvMapResetExisting = false;

  // IT-F2-407 (Mike c/4e907c83 2026-10-21): when true, Confirm also fires
  // a welcome email for every existing user that gained a new entitlement
  // ("+N entitlement" rows). Mirrors the reset-existing checkbox pattern
  // above; the welcome-email send goes through the standard
  // /rest/welcome-email/send router so magic-link vs activation mode is
  // picked per-recipient (same as the Users → Actions → Send Welcome
  // Email flow).
  csvMapWelcomeEntitled = false;

  // IT-F2-407 (Mike 2026-09-18 c/7eb9ef8e): batch-level role picker for
  // the bulk import. Per-row `Role` column mapping still overrides. Default
  // 'member' matches pre-picker behavior — nobody bulk-imports admins.
  csvMapRole = 'member';

  async processImportUsers(event: any) {
    try {
      const csvFile = event.target.files?.[0];
      if (!csvFile) return;
      // Parse without header so we get raw string[][] — the mapping dialog
      // decides which row is the header and how columns map.
      const parsed: any = await this.ngxCsvParser
        .parse(csvFile, { header: false, delimiter: ',' })
        .pipe().toPromise();
      const raw: string[][] = (Array.isArray(parsed) ? parsed : [])
        .map((row: any) => Array.isArray(row) ? row.map((v: any) => String(v ?? '')) : [])
        .filter((row: string[]) => row.some(v => String(v ?? '').trim() !== ''));
      if (raw.length === 0) {
        this.toastService.error('CSV import', 'File is empty or all rows are blank.');
        return;
      }
      // Normalize row width — pad short rows to the widest so the dropdown
      // column count is consistent (trailing empties are common).
      const width = raw.reduce((m, r) => Math.max(m, r.length), 0);
      for (const r of raw) {
        while (r.length < width) r.push('');
      }
      // Auto-detect header row: if row[0] has any cell that looks like a
      // field name (Email/FirstName/etc.), assume header:true. Otherwise
      // header:false and start from row 0.
      const looksLikeHeader = (row: string[]): boolean => {
        const hints = ['email','first','last','name','role','scanner','user','id'];
        const lower = row.map(v => String(v || '').trim().toLowerCase());
        return lower.some(v => hints.some(h => v === h || v.includes(h)));
      };
      this.csvMapRawRows = raw;
      this.csvMapHasHeader = looksLikeHeader(raw[0]);
      this.csvMapColumnMapping = this._auto_map_csv_columns(raw, this.csvMapHasHeader);
      this.csvMapDialogOpen = true;
      // Fire inline preflight if a sticky customer was picked from a prior import.
      void this._run_preview_preflight();
    } catch (e: any) {
      console.error('processImportUsers() => ', e);
      this.toastService.error('CSV parse failed', String(e?.message || e));
    }
  }

  // Public (called from template `(ngModelChange)` on the "First row is header" toggle).
  _auto_map_csv_columns(rows: string[][], hasHeader: boolean): string[] {
    const width = rows[0]?.length || 0;
    const mapping = new Array(width).fill('Ignore');
    if (rows.length === 0) return mapping;

    // Step 1 — header-name lookup (only if hasHeader). Case-insensitive,
    // ignores whitespace/underscores/hyphens.
    if (hasHeader) {
      const header = rows[0];
      const rules: Array<[RegExp, string]> = [
        [/^email(address)?$/,   'Email'],
        [/^first(name)?$/,      'First Name'],
        [/^last(name)?$/,       'Last Name'],
        [/^(family)?name$/,     'Last Name'],
        [/^role$/,              'Role'],
        [/^customer(slug)?$/,   'Customer'],
        [/^scanners?$/,         'Scanners'],
      ];
      for (let i = 0; i < width; i++) {
        const h = String(header[i] || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
        for (const [re, field] of rules) {
          if (re.test(h)) { mapping[i] = field; break; }
        }
      }
    }

    // Step 2 — regex data-sampling fallback for Email column (Mike
    // 2026-09-18 c/7c88f246: "try some regex to detect if a column
    // contains a email address pre select email as a proposed field").
    // Sample up to 10 data rows, tally which columns look like emails,
    // pick the column with the highest hit count (>=50% of non-empty
    // values). Works for both hasHeader=true (fills in missing Email
    // when header is unrecognized) and hasHeader=false (Bulk Add path,
    // where every row is an email with no header).
    if (!mapping.includes('Email')) {
      const start = hasHeader ? 1 : 0;
      const sample = rows.slice(start, start + 10);
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      let bestCol = -1;
      let bestHits = 0;
      for (let i = 0; i < width; i++) {
        if (mapping[i] !== 'Ignore') continue;
        let hits = 0;
        let total = 0;
        for (const row of sample) {
          const v = String(row?.[i] || '').trim();
          if (v) total++;
          if (v && emailRegex.test(v)) hits++;
        }
        if (total > 0 && hits / total >= 0.5 && hits > bestHits) {
          bestCol = i;
          bestHits = hits;
        }
      }
      if (bestCol >= 0) mapping[bestCol] = 'Email';
    }

    return mapping;
  }

  csvMapCancel() {
    this.csvMapDialogOpen = false;
    this.csvMapRawRows = [];
    this.csvMapColumnMapping = [];
    this.csvMapCustomer = '';
    this.csvMapScanners = [];
  }

  get csvMapEmailColumnPicked(): boolean {
    return this.csvMapColumnMapping.includes('Email');
  }

  // IT-F2-407: dynamic scanner list filtered to the picked default
  // customer, so the multi-select stays scoped to that customer's scanners.
  get csvMapScannersForCustomer(): any[] {
    const slug = String(this.csvMapCustomer || '').trim();
    if (!slug) return [];
    return (this.scanners || []).filter((s: any) => String(s?.Client || '') === slug);
  }

  csvMapToggleScanner(id: string) {
    const idx = this.csvMapScanners.indexOf(id);
    if (idx >= 0) this.csvMapScanners.splice(idx, 1);
    else this.csvMapScanners.push(id);
  }

  csvMapOnCustomerChange() {
    // When default customer changes, drop any previously-selected scanner
    // that doesn't belong to the new customer.
    const allowed = new Set(this.csvMapScannersForCustomer.map((s: any) => String(s._id || s.Id || '')));
    this.csvMapScanners = this.csvMapScanners.filter(s => allowed.has(s));
    // Re-run the inline pre-flight against the new target pool so preview
    // badges reflect the picked customer's identities.
    void this._run_preview_preflight();
  }

  csvMapOnHeaderToggle() {
    // Header toggle shifts which rows count as data rows — re-run preflight.
    this.csvMapColumnMapping = this._auto_map_csv_columns(this.csvMapRawRows, this.csvMapHasHeader);
    void this._run_preview_preflight();
  }

  csvMapOnColumnMappingChange() {
    // Column mapping change might move the Email column — re-run preflight.
    void this._run_preview_preflight();
  }

  /** Whether a specific email in the current data set is flagged as already
   *  existing in the target Cognito pool. Consumed by the preview table
   *  template. */
  csvMapEmailExistsInPool(email: string): boolean {
    return this.csvMapExistingInPool.has(String(email || '').trim().toLowerCase());
  }

  /** Extract the email cell from a preview row using the current Email
   *  column mapping. Empty string if no Email column mapped. */
  csvMapRowEmail(row: string[]): string {
    const width = this.csvMapRawRows[0]?.length || 0;
    for (let i = 0; i < width; i++) {
      if (this.csvMapColumnMapping[i] === 'Email') return String(row?.[i] || '').trim();
    }
    return '';
  }

  /** Count of preview-emails already present in the target pool. */
  get csvMapExistingCount(): number {
    return this.csvMapExistingInPool.size;
  }

  /** IT-F2-407 c/4e907c83 — count of preview rows that will trigger an
   *  entitlement PATCH (existing user missing >=1 selected scanner or the
   *  batch customer). Used by the welcome-email checkbox label. */
  get csvMapEntitlementCount(): number {
    if (this.csvMapExistingInPool.size === 0) return 0;
    let n = 0;
    for (const em of this.csvMapExistingInPool) {
      if (this.csvMapRowAddScannerCount(em) > 0) n++;
    }
    return n;
  }

  /** Total emails in the current mapped data set (post header-toggle). */
  get csvMapTotalEmailCount(): number {
    const width = this.csvMapRawRows[0]?.length || 0;
    let emailIdx = -1;
    for (let i = 0; i < width; i++) {
      if (this.csvMapColumnMapping[i] === 'Email') { emailIdx = i; break; }
    }
    if (emailIdx < 0) return 0;
    const start = this.csvMapHasHeader ? 1 : 0;
    return this.csvMapRawRows.slice(start).filter(r => String(r[emailIdx] || '').trim() !== '').length;
  }

  /** Run parallel /lookup-pools-for-email against every currently-mapped
   *  email, populating csvMapExistingInPool with those that resolve to the
   *  current target pool. Seq-guarded so the last invocation wins if the
   *  operator flips customer/header rapidly. */
  private async _run_preview_preflight(): Promise<void> {
    const seq = ++this._csvMapPreflightSeq;
    const targetPool = this._csv_target_pool_id();
    // No customer picked → clear flags (legacy-pool preflight is intentionally
    // suppressed here; legacy has too many identities to flag usefully).
    if (!this.csvMapCustomer) {
      this.csvMapExistingInPool = new Set();
      this.csvMapPreflightForPool = '';
      this.csvMapPreflightRunning = false;
      return;
    }
    const width = this.csvMapRawRows[0]?.length || 0;
    let emailIdx = -1;
    for (let i = 0; i < width; i++) {
      if (this.csvMapColumnMapping[i] === 'Email') { emailIdx = i; break; }
    }
    if (emailIdx < 0) {
      this.csvMapExistingInPool = new Set();
      this.csvMapPreflightForPool = '';
      this.csvMapPreflightRunning = false;
      return;
    }
    const start = this.csvMapHasHeader ? 1 : 0;
    const emails = this.csvMapRawRows.slice(start)
      .map(r => String(r[emailIdx] || '').trim().toLowerCase())
      .filter(e => e && e.includes('@'));
    if (emails.length === 0) {
      this.csvMapExistingInPool = new Set();
      this.csvMapPreflightForPool = targetPool;
      this.csvMapPreflightRunning = false;
      return;
    }
    this.csvMapPreflightRunning = true;
    try {
      const hits = await this._csv_preflight_lookup(emails, targetPool);
      if (seq !== this._csvMapPreflightSeq) return;   // superseded
      this.csvMapExistingInPool = hits;
      this.csvMapPreflightForPool = targetPool;
      // IT-F2-407 cid f9d728a2 — second pass to grab current scanners+customers
      // for every existing hit so csvMapRowStatus() can distinguish "already
      // fully entitled" (skip) from "will add N new scanners" (PATCH).
      await this._csv_preflight_existing_meta(Array.from(hits), targetPool);
      if (seq !== this._csvMapPreflightSeq) return;
    } catch {
      if (seq !== this._csvMapPreflightSeq) return;
      this.csvMapExistingInPool = new Set();
      this.csvMapExistingUsersMeta.clear();
      this.csvMapPreflightForPool = targetPool;
    } finally {
      if (seq === this._csvMapPreflightSeq) this.csvMapPreflightRunning = false;
    }
  }

  get csvMapPreviewRows(): string[][] {
    // Show up to 5 data rows in the preview; header (when present) shows
    // separately in the mapping row.
    const start = this.csvMapHasHeader ? 1 : 0;
    return this.csvMapRawRows.slice(start, start + 5);
  }

  csvMapColumnLabel(i: number): string {
    // Header label if present, else "Column N".
    if (this.csvMapHasHeader && this.csvMapRawRows[0]) {
      const h = String(this.csvMapRawRows[0][i] || '').trim();
      if (h) return h;
    }
    return `Column ${i + 1}`;
  }

  // IT-F2-407 (Mike 2026-09-18): pre-flight duplicate check against the
  // target pool before firing the create loop. Uses GET /users/lookup-
  // pools-for-email per email in parallel; any email whose returned
  // pool_ids includes the target pool is flagged as a duplicate.
  // Operator gets a confirm modal to skip existing + import the rest, or
  // cancel. Legacy pool id when no per-customer pool is set — matches the
  // backend's fallback (userPool default = us-east-1_0Q00rhHoS).
  private readonly LEGACY_POOL_ID = 'us-east-1_0Q00rhHoS';
  private _csv_target_pool_id(): string {
    const slug = String(this.csvMapCustomer || '').trim();
    if (!slug) return this.LEGACY_POOL_ID;
    const cust = (this.directoryCustomers || []).find((c: any) => String(c?._id || '') === slug);
    const pid = String(cust?.cognito_pool?.user_pool_id || '').trim();
    return pid || this.LEGACY_POOL_ID;
  }

  // IT-F2-407 (Mike 2026-09-18: "the add user should know ses isn't
  // setup"). Heuristic: per-customer pools are configured with branded
  // SES (that's why the customer got a dedicated pool); the legacy pool
  // uses Cognito default sender. If the picked customer has NO
  // cognito_pool.user_pool_id, we're routing to the legacy pool, which
  // means the invitee will get Cognito's default temp-password email
  // (not customer-branded). Show a warning banner in the modal so the
  // operator knows before hitting Import.
  get csvMapTargetPoolBrandedSes(): boolean {
    const slug = String(this.csvMapCustomer || '').trim();
    if (!slug) return false;    // no customer → legacy pool → no branded SES
    const cust = (this.directoryCustomers || []).find((c: any) => String(c?._id || '') === slug);
    return !!(cust && cust.cognito_pool && cust.cognito_pool.user_pool_id);
  }
  private async _csv_preflight_lookup(emails: string[], targetPool: string): Promise<Set<string>> {
    const existing = new Set<string>();
    // Serialize into small parallel batches — the endpoint is cheap
    // (Redis cache read), but we don't want to open 1000 sockets at once.
    const BATCH = 10;
    for (let i = 0; i < emails.length; i += BATCH) {
      const chunk = emails.slice(i, i + BATCH);
      const results = await Promise.all(chunk.map(async (email) => {
        try {
          // adminService.lookupPoolsForEmail already unwraps res.pool_ids
          // → returns string[] directly. Prior code re-checked res?.pool_ids
          // which is undefined on a plain array, so pools was always []
          // and every email was reported as new. Mike 2026-09-18 c/57a502dc
          // caught it (thebugger2000@yahoo.com flagged NEW despite being in
          // the OXC pool grid).
          const pools = await this.adminService.lookupPoolsForEmail(email);
          const list: string[] = Array.isArray(pools) ? pools : [];
          return { email, exists_in_target: list.includes(targetPool) };
        } catch {
          return { email, exists_in_target: false };
        }
      }));
      for (const r of results) if (r.exists_in_target) existing.add(String(r.email || '').toLowerCase());
    }
    return existing;
  }

  // IT-F2-407 (Mike 2026-10-20 cid f9d728a2) — second preflight pass.
  // For every email flagged existing in the target pool, resolve the user's
  // current custom:scanners + custom:customers + Cognito Username so
  // csvMapRowStatus() can distinguish "has all the selected entitlements"
  // (no change) from "missing some" (PATCH to add), and so the PATCH loop
  // can actually address the right Cognito identity.
  //
  // Resolution order per email:
  //   1. Local this.data cache (admin was already looking at this user)
  //   2. adminService.getCachedUser(email, poolId) — hits hGet(email) which
  //      works for per-customer-pool users (AdminCreateUser → username=email)
  //      but misses for legacy-pool users whose username is a UUID sub.
  //
  // If BOTH fail, we DON'T set a meta entry — csvMapRowStatus's fallback is
  // now optimistic (treat as 'add_scanners' with full count) so the operator
  // sees SOMETHING to look at rather than a silent "No change". The PATCH
  // loop safely skips rows with no meta (reports as entitled_failed).
  //
  // Mike c/c2b85b97 2026-10-21: previous ship (7dda26f) returned no meta for
  // member-support@f2-tech.com in the Option Sniper own-pool because the
  // cache HGET by email missed — status fell through to 'no_change'.
  private async _csv_preflight_existing_meta(existingEmails: string[], targetPool: string): Promise<void> {
    this.csvMapExistingUsersMeta.clear();
    if (existingEmails.length === 0) return;

    const resolveFromLocal = (email: string): any | null => {
      const lower = String(email || '').trim().toLowerCase();
      if (!lower) return null;
      for (const u of (this.data || [])) {
        const em = String(u?.attributes?.email || u?.email || '').trim().toLowerCase();
        if (em !== lower) continue;
        if (targetPool && String(u?.pool_id || '') !== targetPool) continue;
        return u;
      }
      return null;
    };

    const toMeta = (u: any, email: string, poolId: string) => {
      const attrs: any = u?.attributes || {};
      const scannersCsv = String(attrs['custom:scanners'] || '').trim();
      const customersCsv = String(attrs['custom:customers'] || '').trim();
      return {
        email: String(email).toLowerCase(),
        username: String(u?.username || email),
        pool_id: String(u?.pool_id || poolId),
        scanners: new Set<string>(scannersCsv.split(',').map((s: string) => s.trim()).filter(Boolean)),
        customers: new Set<string>(customersCsv.split(',').map((s: string) => s.trim()).filter(Boolean)),
      };
    };

    const BATCH = 10;
    for (let i = 0; i < existingEmails.length; i += BATCH) {
      const chunk = existingEmails.slice(i, i + BATCH);
      const results = await Promise.all(chunk.map(async (email) => {
        // Try local cache first — zero network cost. Admin is almost always
        // viewing the users-grid for the target customer when they open
        // Bulk Add, so this.data usually has the row.
        const local = resolveFromLocal(email);
        if (local) return toMeta(local, email, targetPool);
        try {
          const u: any = await this.adminService.getCachedUser(email, targetPool);
          if (!u) {
            console.warn(`[Bulk Add preflight] no user doc for ${email} in pool ${targetPool} ` +
              `(hGet by email missed — likely a legacy-pool UUID-username row). ` +
              `Row will show optimistic +N and the PATCH loop will log the resolution failure.`);
            return null;
          }
          return toMeta(u, email, targetPool);
        } catch (e: any) {
          console.warn(`[Bulk Add preflight] getCachedUser(${email}) threw: ${e?.message || e}`);
          return null;
        }
      }));
      for (const r of results) {
        if (!r) continue;
        this.csvMapExistingUsersMeta.set(r.email, {
          username: r.username,
          pool_id: r.pool_id,
          scanners: r.scanners,
          customers: r.customers,
        });
      }
    }
  }

  // IT-F2-407 (Mike 2026-10-20 cid f9d728a2) — per-row status for the
  // preview grid. Three states:
  //   'new'            — email doesn't exist in target pool; will CREATE
  //   'add_scanners'   — exists but missing >=1 scanner that this import adds
  //   'no_change'      — exists and already has all selected scanners+customer
  // Returns empty string before the preflight has resolved (badge hidden).
  //
  // Mike c/c2b85b97 2026-10-21: fallback for "existing + no meta" is now
  // OPTIMISTIC — assume missing all selected entitlements and surface the
  // row as 'add_scanners'. Previously fell through to 'no_change', hiding
  // the real add from the operator (Mike's screenshot of member-support
  // flagged No Change despite not having the Option Sniper scanner). The
  // PATCH loop refuses rows with no meta so a bogus +N doesn't corrupt
  // Cognito — it just reports as entitled_failed in the final toast.
  csvMapRowStatus(email: string): 'new' | 'add_scanners' | 'no_change' | '' {
    const em = String(email || '').trim().toLowerCase();
    if (!em || !this.csvMapCustomer) return '';
    if (this.csvMapPreflightRunning) return '';
    if (!this.csvMapExistingInPool.has(em)) return 'new';
    return this.csvMapRowAddScannerCount(em) > 0 ? 'add_scanners' : 'no_change';
  }

  // IT-F2-407 cid f9d728a2 — count of selected scanners the existing user
  // doesn't have yet. Also factors in the batch customer slug: if the
  // user's custom:customers doesn't carry the picked customer, that counts
  // as +1 missing entitlement too (surfaced in the badge as "+customer").
  //
  // When no meta was resolved for this email (preflight hGet miss), assume
  // the user has NONE of the selected entitlements — the badge shows the
  // full delta count and the operator can decide whether to proceed.
  csvMapRowAddScannerCount(email: string): number {
    const em = String(email || '').trim().toLowerCase();
    const meta = this.csvMapExistingUsersMeta.get(em);
    const selected = this.csvMapScanners || [];
    if (!meta) {
      // No resolved entitlements — treat all selected scanners + batch
      // customer as "missing" so the +N badge surfaces the row.
      let miss = selected.filter(s => !!s).length;
      if (this.csvMapCustomer) miss++;
      return miss;
    }
    let miss = 0;
    for (const s of selected) if (s && !meta.scanners.has(s)) miss++;
    if (this.csvMapCustomer && !meta.customers.has(this.csvMapCustomer)) miss++;
    return miss;
  }

  async csvMapConfirmImport() {
    if (!this.csvMapEmailColumnPicked || this.csvMapImporting) return;
    const width = this.csvMapRawRows[0]?.length || 0;
    // Field → column-index reverse map.
    const idx: Record<string, number> = {};
    for (let i = 0; i < width; i++) {
      const field = this.csvMapColumnMapping[i];
      if (field && field !== 'Ignore' && idx[field] === undefined) idx[field] = i;
    }
    const allDataRows = this.csvMapHasHeader ? this.csvMapRawRows.slice(1) : this.csvMapRawRows.slice(0);

    // Pre-flight: check which emails already exist in the target pool.
    const emailList = allDataRows
      .map(r => String(r[idx['Email']] || '').trim().toLowerCase())
      .filter(e => e);
    if (emailList.length === 0) {
      this.toastService.error('CSV import', 'No email values in the mapped column.');
      return;
    }
    // IT-F2-407 (Mike 2026-09-18 c/ac87641f): explicit customer pick is
    // required. Empty picker routes to the legacy pool, which almost
    // always collides with existing users on a bulk paste (the same
    // email frequently already exists there from a different customer).
    // Force the operator to acknowledge OR pick a customer.
    if (!this.csvMapCustomer) {
      const proceed = window.confirm(
        'No customer picked — users will be created in the legacy pool ' +
        '(no branded email, high risk of "already exists" collisions with ' +
        'other customers\' users).\n\n' +
        'Click OK to proceed with legacy pool, or Cancel to go back and pick a customer.'
      );
      if (!proceed) {
        this.toastService.error('CSV import', 'Cancelled — pick a customer and retry.');
        return;
      }
    }
    const targetPool = this._csv_target_pool_id();
    let existing: Set<string>;
    // Reuse the inline preview preflight if it ran against the same pool —
    // saves a duplicate fan-out round when the operator just picked a
    // customer, watched the badges land, then hit Import. Otherwise fall
    // back to a fresh scan.
    if (this.csvMapPreflightForPool === targetPool && !this.csvMapPreflightRunning) {
      existing = new Set(this.csvMapExistingInPool);
    } else {
      this.csvMapImporting = true;      // reuse the spinner during the pre-flight scan
      this.csvMapImportTotal = emailList.length;
      this.csvMapImportProgress = 0;
      try {
        existing = await this._csv_preflight_lookup(emailList, targetPool);
      } catch (e: any) {
        this.csvMapImporting = false;
        this.toastService.error('CSV import pre-flight failed', String(e?.message || e));
        return;
      }
      this.csvMapImporting = false;
    }

    let dataRows = allDataRows;
    // IT-F2-407 (Mike 2026-09-18 c/2f26d0db): when the operator ticks
    // "send reset email to already-exists", the existing rows aren't
    // skipped silently — we fire an AdminResetUserPassword against the
    // target pool for each Exists email BEFORE the create loop starts.
    // Cognito sends the customer-branded (or default, per SES config)
    // reset-code email. Failures are counted and included in the
    // completion toast so the operator knows what didn't go through.
    let reset_sent = 0;
    let reset_failed = 0;
    if (existing.size > 0 && this.csvMapResetExisting) {
      this.csvMapImporting = true;
      this.csvMapImportTotal = existing.size;
      this.csvMapImportProgress = 0;
      for (const em of existing) {
        try {
          const r = await this.adminService.resetPassword(em, targetPool);
          if (r && !r.err) reset_sent++; else reset_failed++;
        } catch { reset_failed++; }
        this.csvMapImportProgress++;
      }
      this.csvMapImporting = false;
    }
    // IT-F2-407 cid f9d728a2 — pull meta for existing-user entitlement adds
    // alongside the base preflight so the import executor can PATCH scanners
    // onto existing users instead of silently skipping them. Only runs when
    // the base lookup already identified existing emails.
    if (existing.size > 0 && this.csvMapExistingUsersMeta.size === 0) {
      try {
        await this._csv_preflight_existing_meta(Array.from(existing), targetPool);
      } catch { /* non-fatal; falls through to skip-only behavior below */ }
    }
    // Separate existing rows into "will add entitlement" (dirty meta) vs
    // "no change" (fully entitled). The create loop still skips both — this
    // split only drives the add-entitlement loop at the end.
    const addEntitlementRows: string[][] = [];
    if (existing.size > 0) {
      // Drop existing emails from the create-loop data set regardless of
      // reset choice — creating a duplicate would hard-error from Cognito.
      dataRows = allDataRows.filter(r => {
        const em = String(r[idx['Email']] || '').trim().toLowerCase();
        return em && !existing.has(em);
      });
      for (const r of allDataRows) {
        const em = String(r[idx['Email']] || '').trim().toLowerCase();
        if (!em || !existing.has(em)) continue;
        if (this.csvMapRowAddScannerCount(em) > 0) addEntitlementRows.push(r);
      }
      // If we didn't send resets AND there are no fresh rows to create AND
      // no existing users will gain entitlements, confirm/abort so a
      // purely-duplicate batch doesn't silently no-op.
      if (!this.csvMapResetExisting && dataRows.length === 0 && addEntitlementRows.length === 0) {
        const msg = `${existing.size} of ${emailList.length} emails already exist in the target pool.\n\nAll rows are duplicates with full entitlements — nothing to create or add. Click OK to close, or tick "Send reset email" and try again.`;
        window.alert(msg);
        return;
      }
    }

    this.csvMapImporting = true;
    this.csvMapImportTotal = dataRows.length;
    this.csvMapImportProgress = 0;
    let created = 0;
    let failed = 0;
    // IT-F2-407: track users the backend downgraded from magic-link to
    // no-email (legacy pool lacks branded SES config). Report the count in
    // the completion toast so the operator knows to send welcome emails
    // manually via Actions → Send Welcome Email.
    let no_invite_sent = 0;
    try {
      const batchCustomer = String(this.csvMapCustomer || '').trim();
      const batchScannersCsv = (this.csvMapScanners || []).join(',');
      for (const row of dataRows) {
        const email = String(row[idx['Email']] || '').trim();
        if (!email) { failed++; this.csvMapImportProgress++; continue; }
        const first = idx['First Name'] !== undefined ? String(row[idx['First Name']] || '').trim() : '';
        const last  = idx['Last Name']  !== undefined ? String(row[idx['Last Name']]  || '').trim() : '';
        const role  = idx['Role']       !== undefined ? String(row[idx['Role']]      || '').trim() : '';
        const rowCustomer = idx['Customer'] !== undefined ? String(row[idx['Customer']] || '').trim() : '';
        const rowScanners = idx['Scanners'] !== undefined ? String(row[idx['Scanners']] || '').trim() : '';
        // Per-row (from CSV column) wins over batch default; batch default
        // applies when the CSV row didn't supply a value. Batch-level
        // role picker (csvMapRole) is the default when neither a Role
        // column nor a mapped value is supplied. Mike 2026-09-18 c/7eb9ef8e.
        const customers = rowCustomer || batchCustomer;
        const scanners  = rowScanners || batchScannersCsv;
        const effectiveRole = role || this.csvMapRole || 'member';
        const needsMagicLink = !first || !last;
        const new_user: any = {
          email,
          name: first || undefined,
          last_name: last || undefined,
          role: effectiveRole,
          customers: customers || undefined,
          scanners: scanners || '',
          send_magic_link: needsMagicLink ? true : undefined,
        };
        const res = await this.adminService.createUser(new_user);
        if (res && !res.err) {
          created++;
          // IT-F2-407: warnings the backend may stamp on a successful create.
          // 'cognito_default_email_sent' = pool lacks branded SES; user got
          // Cognito's default temp-password email instead of branded magic-link.
          // 'no_branded_ses_send_manually' = legacy warning (pre-2026-09-18);
          // treat both as "note to operator" — count separately for the toast.
          if (res.warning === 'cognito_default_email_sent' || res.warning === 'no_branded_ses_send_manually') no_invite_sent++;
          // IT-F2-407 (Mike 2026-09-18 c/ab41dd3d): apply the new row to the
          // grid live per-iteration. Prior code only pushed to this.data and
          // waited for the post-loop setGridOption call, so a bulk import
          // showed a frozen grid until the whole batch completed — Mike had
          // to reload to see partial progress. SSE would eventually deliver
          // the same row via F2:USER_UPDATES publish → applyTransaction, but
          // that's a longer round-trip AND fails silently if the SSE stream
          // is asleep. Prepend (not push) so newest lands on top like the
          // SSE handler does.
          this.data = [res, ...this.data];
          try {
            const api = this.grid?.api;
            const rowId = `${res.pool_id || ''}::${res.username || ''}`;
            const existing = rowId !== '::' ? api?.getRowNode?.(rowId) : null;
            if (existing) {
              api?.applyTransaction?.({ update: [res] });
            } else {
              api?.applyTransaction?.({ add: [res], addIndex: 0 });
            }
          } catch { /* non-fatal — post-loop setGridOption still fires */ }
        } else {
          failed++;
        }
        this.csvMapImportProgress++;
      }
      // IT-F2-407 cid f9d728a2 — second loop: PATCH entitlements onto
      // existing users (those we flagged as "+N entitlement" in the
      // preview). Union-adds the batch customer + selected scanners onto
      // the user's current custom:customers / custom:scanners CSVs; the
      // backend's updateUserAttributes writes into the correct per-row
      // pool_id (resolved from the preflight meta — avoids the legacy-pool
      // fallthrough that bites when the same email exists in multiple
      // pools).
      //
      // Mike c/c2b85b97 2026-10-21: if preflight failed to resolve meta,
      // fall back to email-as-username with the batch targetPool. Works
      // for alias-enabled per-customer pools (AdminCreateUser → username
      // = email). Logs any resolution failure so the operator can chase
      // the stragglers in /admin/error-reports.
      let entitled = 0;
      let entitled_failed = 0;
      let entitled_meta_missing = 0;
      // IT-F2-407 c/4e907c83 — collect successful entitlement rows for the
      // optional welcome-email send that runs after the PATCH loop.
      const entitledForWelcome: WelcomeEmailUserInput[] = [];
      if (addEntitlementRows.length > 0) {
        const batchCustomerForMerge = String(this.csvMapCustomer || '').trim();
        const batchScannersForMerge = this.csvMapScanners || [];
        this.csvMapImportTotal += addEntitlementRows.length;
        for (const row of addEntitlementRows) {
          const em = String(row[idx['Email']] || '').trim().toLowerCase();
          const meta = this.csvMapExistingUsersMeta.get(em);
          // Resolve the Cognito identity. meta is the happy path; the
          // fallback uses email as username with the batch targetPool.
          const resolveUsername = meta?.username || em;
          const resolvePoolId = meta?.pool_id || targetPool;
          const currentScanners = meta?.scanners || new Set<string>();
          const currentCustomers = meta?.customers || new Set<string>();
          if (!meta) entitled_meta_missing++;
          const nextScanners = new Set<string>(currentScanners);
          for (const s of batchScannersForMerge) if (s) nextScanners.add(s);
          const nextCustomers = new Set<string>(currentCustomers);
          if (batchCustomerForMerge) nextCustomers.add(batchCustomerForMerge);
          try {
            const r = await this.adminService.updateUserAttributes(resolveUsername, {
              attributes: [
                { Name: 'custom:scanners', Value: Array.from(nextScanners).sort().join(',') },
                { Name: 'custom:customers', Value: Array.from(nextCustomers).sort().join(',') },
              ],
            }, resolvePoolId);
            if (r && !r.err) {
              entitled++;
              // Queue for welcome-email send if the checkbox is ticked.
              // customer_slug comes from the batch; scanner_ids reflects
              // the merged set so the backend's magic-link vs activation
              // router sees the full post-PATCH entitlement.
              if (this.csvMapWelcomeEntitled && batchCustomerForMerge) {
                entitledForWelcome.push({
                  email: em,
                  username: resolveUsername,
                  pool_id: resolvePoolId,
                  customer_slug: batchCustomerForMerge,
                  scanner_ids: Array.from(nextScanners).sort(),
                });
              }
            } else {
              entitled_failed++;
              console.warn(`[Bulk Add entitlement] PATCH ${resolveUsername} (pool ${resolvePoolId}) rejected: ${r?.err || '(no err field)'}`);
            }
          } catch (e: any) {
            entitled_failed++;
            console.warn(`[Bulk Add entitlement] PATCH ${resolveUsername} threw: ${e?.message || e}`);
          }
          this.csvMapImportProgress++;
        }
      }

      // IT-F2-407 c/4e907c83 — welcome-email send for entitled-existing
      // users. Fires AFTER the PATCH loop so the backend sees the newly-
      // added scanners when it picks the welcome variant per recipient.
      // Non-fatal: a failed send only shows in the toast; the entitlements
      // are already applied in Cognito.
      let welcome_sent = 0;
      let welcome_failed = 0;
      let welcome_skipped = 0;
      if (this.csvMapWelcomeEntitled && entitledForWelcome.length > 0) {
        try {
          const sendRes = await this.welcomeEmailService.send(entitledForWelcome);
          if (sendRes) {
            welcome_sent = sendRes.sent || 0;
            welcome_failed = sendRes.failed || 0;
            welcome_skipped = sendRes.skipped || 0;
          } else {
            welcome_failed = entitledForWelcome.length;
          }
        } catch (e: any) {
          welcome_failed = entitledForWelcome.length;
          console.warn(`[Bulk Add welcome] send threw: ${e?.message || e}`);
        }
      }

      this.grid?.api?.setGridOption?.('rowData', this.data);
      this.csvMapDialogOpen = false;
      this.csvMapRawRows = [];
      this.csvMapColumnMapping = [];
      const invitedHint = no_invite_sent > 0
        ? ` (${no_invite_sent} used Cognito default email — pool lacks branded SES config)`
        : '';
      const resetHint = reset_sent > 0 || reset_failed > 0
        ? ` · reset emails: ${reset_sent} sent${reset_failed > 0 ? `, ${reset_failed} failed (see /admin/error-reports)` : ''}`
        : '';
      // IT-F2-407 cid f9d728a2 — surface the entitlement-add count in the toast.
      // Mike c/c2b85b97 — also surface the meta-missing fallback count so
      // the operator knows how many rows went through the email-as-username
      // guess (may have failed silently in legacy-pool UUID-username cases).
      const metaMissingHint = entitled_meta_missing > 0
        ? ` [${entitled_meta_missing} with preflight-cache miss]`
        : '';
      const entitlementHint = entitled > 0 || entitled_failed > 0
        ? ` · ${entitled} existing user${entitled === 1 ? '' : 's'} entitled to new scanners${entitled_failed > 0 ? ` (${entitled_failed} failed${metaMissingHint})` : metaMissingHint}`
        : '';
      // IT-F2-407 c/4e907c83 — welcome-email send summary when enabled.
      const welcomeHint = (welcome_sent + welcome_failed + welcome_skipped) > 0
        ? ` · welcome emails: ${welcome_sent} sent${welcome_failed > 0 ? `, ${welcome_failed} failed` : ''}${welcome_skipped > 0 ? `, ${welcome_skipped} skipped` : ''}`
        : '';
      if (failed === 0 && reset_failed === 0 && entitled_failed === 0 && welcome_failed === 0) {
        this.toastService.success('CSV import', `Imported ${created} user${created === 1 ? '' : 's'}.${invitedHint}${resetHint}${entitlementHint}${welcomeHint}`);
      } else {
        this.toastService.error('CSV import', `${created} succeeded, ${failed} failed (check console).${invitedHint}${resetHint}${entitlementHint}${welcomeHint}`);
      }
    } catch (e: any) {
      console.error('csvMapConfirmImport() => ', e);
      this.toastService.error('CSV import failed', String(e?.message || e));
    } finally {
      this.csvMapImporting = false;
    }
  }
}
